import { Link, usePage } from "@inertiajs/react"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import {
  ArrowRightLeft,
  Camera,
  Box,
  Clapperboard,
  Copy,
  Download,
  FilePen,
  Film,
  FolderOpen,
  Image as ImageIcon,
  ImageDown,
  Images,
  LayoutGrid,
  Link2Off,
  Unlink,
  Loader2,
  Plus,
  Upload,
  UploadCloud,
  Video as VideoIcon,
} from "lucide-react"
import { confirm } from "@/lib/confirm"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { PostProductionIcon } from "@/components/icons/post-production-icon"
import { AssetPreviewBusyDim, AssetPreviewBusySpinner } from "@/components/media/asset-preview-busy"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { SeedanceDraftBadge, SeedanceDraftFinalizeButton } from "@/components/media/seedance-draft-finalize-controls"
import { ShotVideoResolutionPopover } from "@/components/shot-panel/shot-video-resolution-popover"
import {
  ShotGenerationActions,
  ShotGenerationStatusRow,
  type ShotGenerationStatusView,
} from "@/components/shot-panel/shot-generation-status-row"
import { TimelineEditor } from "@/components/shot-panel/timeline-editor/timeline-editor"
import type { ShotTimelineEditorData } from "@/components/shot-panel/timeline-editor/types"
import { ShotAnimaticSegmentPlayer } from "@/components/shot-panel/shot-animatic-segment-player"
import { SingleClipPlayer, type TimelinePlayerPlaybackApi } from "@/components/timeline-player"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { pipSourcesFromShot } from "@/lib/player-pip"
import { ShotFrameCapturesStrip } from "@/components/shot-panel/shot-frame-captures-strip"
import { TransitionFrameControls, TransitionFrameHalf } from "@/components/shot-panel/transition-frame-half"
import { PostProductionLaunchDialog } from "@/components/shot-panel/post-production-launch-dialog"
import { useShotVideoGenerationSettings } from "@/components/shot-panel/use-shot-video-generation-settings"
import {
  FirstGenResolutionDialog,
  useFirstGenResolutionGate,
} from "@/components/shot-panel/first-gen-resolution-dialog"
import { Button } from "@/components/ui/primitives"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { FloatingTabPill, type FloatingTabPillTab } from "@/components/ui/floating-tab-pill"
import { IconButtonGroup } from "@/components/ui/icon-button-group"
import { VideoDurationPopover } from "@/components/ui/video-duration-popover"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { SketchDialog, type SketchCorrectionPendingChange } from "@/components/sketch/sketch-dialog"
import { useSeedanceVideoConsent } from "@/hooks/use-seedance-video-consent"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import {
  activeShotJob,
  inFlightSketchEditForShot,
  jobStatusMarksTabUnread,
  TAB_JOB_KINDS,
  useReloadOnJobFinish,
  type ShotTabJobKey,
} from "@/lib/shot-jobs"
import {
  createShotFrameCapture,
  deleteShotFrameCapture,
  listShotFrameCaptures,
  type ShotFrameCaptureRow,
} from "@/lib/shot-frame-captures-api"
import { ShotReferencesPanel } from "@/components/shot-references-panel"
import { shotRefCharacter, shotRefLocation, shotRefProps } from "@/components/shot-reference-entities"
import { copyImageToClipboard, downloadImage } from "@/lib/download-image"
import { formatBytes } from "@/lib/format-bytes"
import { effectiveFrameFormat, frameFormatClasses, frameFormatRatio } from "@/lib/frame-format"
import { readEpisodeIdFromUrl, workspaceUrlWithEpisode } from "@/lib/episode-timeline"
import { formatMs } from "@/lib/format-ms"
import { formatShotLabel } from "@/lib/shot-label"
import {
  patchShotVideoDraft,
  patchShotVideoResolution,
  patchShotVideoSettings,
  postShotDraftFinalize,
  runWorkspace,
} from "@/lib/inertia-actions"
import { storyboardModelLabel, videoModelLabel } from "@/lib/provider-labels"
import { parseDraftFinalize, seedanceDraftEligible, type DraftFinalize } from "@/lib/seedance-draft"
import {
  effectiveShotVideoModelKey,
  effectiveShotVideoResolution,
  findShotVideoModel,
  inheritedShotVideoResolution,
  resolutionParamOptions,
} from "@/lib/shot-video-settings"
import {
  ADAPTIVE_DURATION_SEC,
  hasVideoReferenceForAdaptive,
  nearestDurationSec,
  nextShotDurationPatch,
  withAdaptiveDurationOption,
} from "@/lib/video-duration"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"
import { ensureShotScaleSheet } from "@/lib/scale-sheet-api"
import { ensureShotPropScaleSheet } from "@/lib/prop-scale-sheet-api"
import { ensureShotMixedScaleSheet } from "@/lib/mixed-scale-sheet-api"
import {
  fetchProjectCast,
  fetchProjectProps,
  fetchShotWorkspaceDetail,
  getCsrfToken,
  patchShotPromptSettings,
  markShotPreviewTabViewed,
  resetShotTransitionFrame,
  setShotAnimaticFromSource,
  setShotShootingBoardFromSource,
  setShotStoryboardFromSource,
  setShotTransitionFrameFromSource,
  setShotVideoFromSource,
  unlinkAnimaticSegment,
  uploadShotAnimatic,
  uploadShotShootingBoard,
  uploadShotStoryboard,
} from "@/lib/workspace-api"
import type { ShotFrameSource, TransitionFramesState, TransitionFrameSide } from "@/lib/workspace-api"
import { animaticSegmentSource, librarySourceKey, previzSource, type LibraryPickMedia } from "@/lib/library-source"
import {
  createPreviz,
  createPrevizFromSource,
  fetchLibraryScene3ds,
  fetchScene3d,
  generatePrevizDepth,
  linkPrevizToShot,
  type LibraryPrevizItem,
  type LibraryScene3dItem,
} from "@/lib/library-api"

import {
  LocationScene3dDialog,
  type LocationScene3dDialogHandle,
} from "@/components/locations/location-scene-3d-dialog"
import { LocationScene3dCreateDialog } from "@/components/locations/location-scene-3d-create-dialog"
import { pickRecentScenes, Scene3dPickerDialog } from "@/components/locations/scene3d-picker-dialog"
import type { CastRow, PropRow } from "@/components/prompt-editor/mention-sources"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import type { GenerationJobRow, WorkspaceBaseProps } from "@/types/workspace"
import type { TimelinePlaybackControls } from "@/lib/timeline"
import { useShotPanelStore, type PrevizDepthVariant } from "@/stores/shot-panel-store"
import { cn } from "@/lib/utils"
import { useInitialLoadReporter } from "@/components/shot-panel/use-initial-load-reporter"
import { useTranslation } from "@/hooks/use-translation"

/** Kind джобы извлечения скриншота (константа — стабильная ссылка для useMemo). */
const FRAME_CAPTURE_JOB_KINDS = ["shot_frame_capture"]

const ANIMATIC_PICK_ACCEPTS: readonly LibraryPickMedia[] = ["animatic", "shot_animatic", "previz", "video"]
const PREVIZ_PICK_ACCEPTS: readonly LibraryPickMedia[] = ["previz", "shot_animatic", "video"]

/**
 * Куда пойдёт выбранный в библиотеке элемент. У обычного шота это опорник, раскадровка
 * или клип видео; у шота-перехода — половина коллажа, у вкладок «Аниматик» и «Превиз» —
 * их видео; пикер один на все случаи, назначения он получает списком.
 */
type FrameLibraryTarget =
  | "storyboard"
  | "shooting_board"
  | "video"
  | "transition_left"
  | "transition_right"
  | "animatic"
  | "previz"
/** Период опроса доступности цели захвата: gap/скрытая дорожка под плейхедом меняются вне React-рендеров. */
const CAPTURE_TARGET_POLL_MS = 500

export type Scene3dEditorSession = {
  open: boolean
  scene3dId: number | null
  name: string
}

type Props = {
  projectId: number
  shotId: number
  jobs: GenerationJobRow[]
  /** Optimistic override from ShotPanel meta; falls back to fetched shot.frame_format. */
  shotFrameFormat?: string | null
  /** Optimistic shot video model override; null inherits project. */
  shotVideoModelKey?: string | null
  /** Optimistic shot video resolution override; null inherits project. */
  shotVideoResolution?: string | null
  onVideoResolutionChange?: (override: string | null) => void
  /** Номер сцены для заголовка плашки подготовки превиза. */
  sceneNumber?: number | null
  refreshKey?: number
  /**
   * Seed from ShotPanel's already-fetched detail — skip the first GET /detail
   * after late mount. refreshKey still forces a network refetch.
   */
  initialShotRaw?: Record<string, unknown> | null
  onDataChanged: () => void
  beforeGenerateVideo?: () => Promise<boolean>
  /** Previz / animatic checkbox is off while the asset exists. false aborts generate. */
  confirmMotionReferences?: () => Promise<boolean>
  extrasSlotRef?: (node: HTMLDivElement | null) => void
  /**
   * Desktop agent column footer for the Frame / Video / Shooting board tab. When set, that
   * tab's Generate is portaled there instead of rendering under the player.
   */
  storyboardGenerateHost?: HTMLElement | null
  videoGenerateHost?: HTMLElement | null
  shootingBoardGenerateHost?: HTMLElement | null
  scene3dDialogRef?: Ref<LocationScene3dDialogHandle>
  onScene3dSessionChange?: (session: Scene3dEditorSession) => void
  onInitialLoadSettled?: () => void
}

type ShotData = {
  id: number
  number: string
  position?: number
  shot_kind: "regular" | "transition"
  intent_summary: string | null
  duration_target_sec: number | null
  frame_format: string | null
  video_model_key: string | null
  video_resolution: string | null
  video_shot_type: string | null
  seedance_adaptive_duration: boolean
  has_video_reference_input: boolean
  storyboard_preview_url: string | null
  storyboard_full_url: string | null
  storyboard_download_url: string | null
  storyboard_full_width: number | null
  storyboard_full_height: number | null
  shooting_board_preview_url: string | null
  shooting_board_pane_url: string | null
  shooting_board_full_url: string | null
  shooting_board_download_url: string | null
  shooting_board_full_width: number | null
  shooting_board_full_height: number | null
  shooting_board_generation_error: string | null
  storyboard_generation_error: string | null
  video_clip_url: string | null
  video_download_url: string | null
  video_file_size: number | null
  timeline_editor: ShotTimelineEditorData | null
  video_generation_error: string | null
  video_generation_provider_transient: boolean
  video_generation_note: string | null
  characters: { id: number; name: string; outfit_id: number | null; outfit_name: string | null }[]
  location_variant_name: string | null
  location_name: string | null
  video_first_frame_url: string | null
  video_last_frame_url: string | null
  prev_shot_last_frame: { scene_number: number; shot_number: string; url: string } | null
  left_last_frame_url: string | null
  right_first_frame_url: string | null
  /** Кадр половины перехода поставлен вручную, а не вычислен по соседнему шоту. */
  transition_left_frame_manual: boolean
  transition_right_frame_manual: boolean
  active_video_version_id: number | null
  video_draft: boolean | null
  active_video_draft_finalize: DraftFinalize | null
  post_production_source_version_id: number | null
  has_animatic_segment: boolean
  hidden: boolean
  animatic_segment: AnimaticSegmentData | null
  /** Привязанный превиз (payload совпадает с плиткой библиотеки). */
  previz: LibraryPrevizItem | null
  previz_depth_variant: PrevizDepthVariant
  location_id: number | null
  reference_images: {
    type: string
    name: string
    subtitle?: string | null
    url: string | null
    download_url?: string | null
    front_view_chrome?: boolean
  }[]
  scale_applicable: boolean
  video_consider_scale: boolean
  scale_sheet_status: string | null
  prop_scale_applicable: boolean
  video_consider_prop_scale: boolean
  prop_scale_sheet_status: string | null
  mixed_scale_applicable: boolean
  mixed_scale_sheet_status: string | null
  /** Server-persisted unread preview tabs (storyboard / video / shooting_board). */
  unread_preview_tabs: ShotTabJobKey[]
}

type AnimaticSegmentData = {
  id: number
  position: number
  start_ms: number
  end_ms: number
  poster_url: string | null
  poster_status: string
  first_frame_url: string | null
  first_frame_status: string
  last_frame_url: string | null
  last_frame_status: string
  reference_clip_status: string
  reference_clip_url: string | null
  reference_clip_stream_url: string | null
  reference_clip_with_audio_status: string
  reference_clip_with_audio_url: string | null
  reference_clip_with_audio_stream_url: string | null
  animatic_video_url: string | null
  animatic_video_stream_url: string | null
  /** Файл, из которого нарезан сегмент. */
  animatic_source_id: number | null
  /** Ролик залит прямо в этом шоте — его можно удалить отсюда целиком. */
  animatic_source_shot_owned: boolean
  animatic_source_status: string | null
  /** Кадры исходного файла для покадровки на таймлайне. */
  timeline_frame_urls: string[]
  timeline_frame_aspect_ratio: number | null
  /** pending — ещё режутся; ready — есть кадры; skipped — кадров не будет. */
  timeline_frames_status: string | null
  animatic_source_duration_ms: number | null
  episode_number: number | null
  season_number: number | null
}

/** Prefer panel meta when defined; `null` means inherit (do not fall back via `??`). */
function optimisticOverride<T>(prop: T | null | undefined, fromShot: T | null | undefined): T | null | undefined {
  return prop !== undefined ? prop : fromShot
}

function parseUnreadPreviewTabs(raw: unknown): ShotTabJobKey[] {
  if (!Array.isArray(raw)) return []
  const allowed = new Set<string>(["storyboard", "video", "shooting_board"])
  return raw.filter((tab): tab is ShotTabJobKey => typeof tab === "string" && allowed.has(tab))
}

function mapShotData(raw: Record<string, unknown>): ShotData {
  const llmExtras = (raw.llm_extras as Record<string, unknown> | null) ?? {}
  const referenceInputs = (raw.shot_reference_inputs as { video?: unknown[] } | null) ?? null
  return {
    id: raw.id as number,
    number: raw.number != null ? String(raw.number) : "",
    shot_kind: (raw.shot_kind as string) === "transition" ? "transition" : "regular",
    intent_summary: (raw.intent_summary as string) ?? null,
    duration_target_sec: (raw.duration_target_sec as number) ?? null,
    frame_format: typeof raw.frame_format === "string" ? raw.frame_format : null,
    video_model_key: typeof raw.video_model_key === "string" ? raw.video_model_key : null,
    video_resolution: typeof raw.video_resolution === "string" ? raw.video_resolution : null,
    video_shot_type: (llmExtras.video_shot_type as string) ?? null,
    seedance_adaptive_duration: Boolean(llmExtras.seedance_adaptive_duration),
    has_video_reference_input: hasVideoReferenceForAdaptive(referenceInputs?.video?.length ?? 0, llmExtras),
    storyboard_preview_url: (raw.storyboard_preview_url as string) ?? null,
    storyboard_full_url: (raw.storyboard_full_url as string) ?? null,
    storyboard_download_url: (raw.storyboard_download_url as string) ?? null,
    storyboard_full_width: (raw.storyboard_full_width as number) ?? null,
    storyboard_full_height: (raw.storyboard_full_height as number) ?? null,
    shooting_board_preview_url: (raw.shooting_board_preview_url as string) ?? null,
    shooting_board_pane_url: (raw.shooting_board_pane_url as string) ?? null,
    shooting_board_full_url: (raw.shooting_board_full_url as string) ?? null,
    shooting_board_download_url: (raw.shooting_board_download_url as string) ?? null,
    shooting_board_full_width: (raw.shooting_board_full_width as number) ?? null,
    shooting_board_full_height: (raw.shooting_board_full_height as number) ?? null,
    shooting_board_generation_error: (llmExtras.shooting_board_generation_error as string) ?? null,
    storyboard_generation_error: (llmExtras.storyboard_generation_error as string) ?? null,
    video_clip_url: (raw.video_clip_url as string) ?? null,
    video_download_url: (raw.video_download_url as string) ?? null,
    video_file_size: typeof raw.video_file_size === "number" ? raw.video_file_size : null,
    timeline_editor: (raw.timeline_editor as ShotTimelineEditorData) ?? null,
    video_generation_error: (llmExtras.video_generation_error as string) ?? null,
    video_generation_provider_transient: llmExtras.video_generation_provider_transient === true,
    video_generation_note: (llmExtras.video_generation_note as string) ?? null,
    characters: Array.isArray(raw.characters) ? (raw.characters as ShotData["characters"]) : [],
    location_variant_name: (raw.location_variant_name as string) ?? null,
    location_name: (raw.location_name as string) ?? null,
    video_first_frame_url: (raw.video_first_frame_url as string) ?? null,
    video_last_frame_url: (raw.video_last_frame_url as string) ?? null,
    prev_shot_last_frame: (() => {
      const rawPrev = raw.prev_shot_last_frame as
        | { scene_number?: unknown; shot_number?: unknown; url?: unknown }
        | null
        | undefined
      if (
        !rawPrev ||
        typeof rawPrev.scene_number !== "number" ||
        (typeof rawPrev.shot_number !== "string" && typeof rawPrev.shot_number !== "number") ||
        typeof rawPrev.url !== "string" ||
        !rawPrev.url.trim()
      ) {
        return null
      }
      return {
        scene_number: rawPrev.scene_number,
        shot_number: String(rawPrev.shot_number),
        url: rawPrev.url,
      }
    })(),
    left_last_frame_url: (raw.left_last_frame_url as string) ?? null,
    right_first_frame_url: (raw.right_first_frame_url as string) ?? null,
    transition_left_frame_manual: raw.transition_left_frame_manual === true,
    transition_right_frame_manual: raw.transition_right_frame_manual === true,
    active_video_version_id: (raw.active_video_version_id as number) ?? null,
    video_draft: raw.video_draft === true ? true : raw.video_draft === false ? false : null,
    active_video_draft_finalize: parseDraftFinalize(raw.active_video_draft_finalize),
    post_production_source_version_id: (raw.post_production_source_version_id as number) ?? null,
    has_animatic_segment: Boolean(raw.has_animatic_segment),
    hidden: raw.hidden === true || raw.status === "hidden",
    animatic_segment: raw.animatic_segment ? mapAnimaticSegment(raw.animatic_segment as Record<string, unknown>) : null,
    previz: raw.previz ? (raw.previz as LibraryPrevizItem) : null,
    previz_depth_variant: llmExtras.seedance_previz_depth_variant === "vis" ? "vis" : "gray",
    location_id: typeof raw.location_id === "number" ? raw.location_id : null,
    reference_images: Array.isArray(raw.reference_images) ? (raw.reference_images as ShotData["reference_images"]) : [],
    scale_applicable: raw.scale_applicable === true,
    video_consider_scale: raw.video_consider_scale !== false,
    scale_sheet_status: typeof raw.scale_sheet_status === "string" ? raw.scale_sheet_status : null,
    prop_scale_applicable: raw.prop_scale_applicable === true,
    video_consider_prop_scale: raw.video_consider_prop_scale !== false,
    prop_scale_sheet_status: typeof raw.prop_scale_sheet_status === "string" ? raw.prop_scale_sheet_status : null,
    mixed_scale_applicable: raw.mixed_scale_applicable === true,
    mixed_scale_sheet_status: typeof raw.mixed_scale_sheet_status === "string" ? raw.mixed_scale_sheet_status : null,
    unread_preview_tabs: parseUnreadPreviewTabs(raw.unread_preview_tabs),
  }
}

function mapAnimaticSegment(raw: Record<string, unknown>): AnimaticSegmentData {
  return {
    id: raw.id as number,
    position: (raw.position as number) ?? 0,
    start_ms: (raw.start_ms as number) ?? 0,
    end_ms: (raw.end_ms as number) ?? 0,
    poster_url: (raw.poster_url as string) ?? null,
    poster_status: (raw.poster_status as string) ?? "pending",
    first_frame_url: (raw.first_frame_url as string) ?? null,
    first_frame_status: (raw.first_frame_status as string) ?? "pending",
    last_frame_url: (raw.last_frame_url as string) ?? null,
    last_frame_status: (raw.last_frame_status as string) ?? "pending",
    reference_clip_status: (raw.reference_clip_status as string) ?? "pending",
    reference_clip_url: (raw.reference_clip_url as string) ?? null,
    reference_clip_stream_url: (raw.reference_clip_stream_url as string) ?? null,
    reference_clip_with_audio_status: (raw.reference_clip_with_audio_status as string) ?? "pending",
    reference_clip_with_audio_url: (raw.reference_clip_with_audio_url as string) ?? null,
    reference_clip_with_audio_stream_url: (raw.reference_clip_with_audio_stream_url as string) ?? null,
    animatic_video_url: (raw.animatic_video_url as string) ?? null,
    animatic_video_stream_url: (raw.animatic_video_stream_url as string) ?? null,
    animatic_source_id: (raw.animatic_source_id as number) ?? null,
    animatic_source_shot_owned: raw.animatic_source_shot_owned === true,
    animatic_source_status: (raw.animatic_source_status as string) ?? null,
    timeline_frame_urls: Array.isArray(raw.timeline_frame_urls) ? (raw.timeline_frame_urls as string[]) : [],
    timeline_frame_aspect_ratio:
      typeof raw.timeline_frame_aspect_ratio === "number" ? raw.timeline_frame_aspect_ratio : null,
    timeline_frames_status: typeof raw.timeline_frames_status === "string" ? raw.timeline_frames_status : null,
    animatic_source_duration_ms:
      typeof raw.animatic_source_duration_ms === "number" ? raw.animatic_source_duration_ms : null,
    episode_number: (raw.episode_number as number) ?? null,
    season_number: (raw.season_number as number) ?? null,
  }
}

export function ShotPreviewView({
  projectId,
  shotId,
  jobs,
  refreshKey,
  shotFrameFormat,
  shotVideoModelKey,
  shotVideoResolution,
  onVideoResolutionChange,
  sceneNumber = null,
  initialShotRaw,
  onDataChanged,
  beforeGenerateVideo,
  confirmMotionReferences,
  extrasSlotRef,
  storyboardGenerateHost = null,
  videoGenerateHost = null,
  shootingBoardGenerateHost = null,
  scene3dDialogRef,
  onScene3dSessionChange,
  onInitialLoadSettled,
}: Props) {
  const { t } = useTranslation(["shot", "common", "jobs", "workspace"])
  const page = usePage<Partial<WorkspaceBaseProps>>()
  const { project, project_permissions, generation_models_by_context, sketch } = useShotPanelWorkspace()
  const animaticsHref = workspaceUrlWithEpisode(
    `/projects/${projectId}/workspace/animatics`,
    readEpisodeIdFromUrl(page.url),
  )
  const { requestSeedanceVideoGeneration, seedanceVideoConsentDialog } = useSeedanceVideoConsent(projectId)
  const [firstGenResolutionOpen, setFirstGenResolutionOpen] = useState(false)
  const previewTab = useShotPanelStore((s) => s.previewTab)
  const setPreviewTab = useShotPanelStore((s) => s.setPreviewTab)
  const setLeftPanel = useShotPanelStore((s) => s.setLeftPanel)
  const previzDepthVariant = useShotPanelStore((s) => s.previzDepthVariant)
  const setPrevizDepthVariant = useShotPanelStore((s) => s.setPrevizDepthVariant)
  const previewTabRef = useRef(previewTab)
  previewTabRef.current = previewTab

  const shootingBoardFileRef = useRef<HTMLInputElement>(null)
  const storyboardFileRef = useRef<HTMLInputElement>(null)
  const videoFileRef = useRef<HTMLInputElement>(null)
  /** Legacy-плеер без timeline_editor — currentTime для захвата кадра. */
  const legacyPlaybackRef = useRef<TimelinePlayerPlaybackApi | null>(null)

  const seededFromPanel =
    initialShotRaw != null && typeof initialShotRaw.id === "number" && initialShotRaw.id === shotId
  const [shot, setShot] = useState<ShotData | null>(() => (seededFromPanel ? mapShotData(initialShotRaw) : null))
  /** Unread preview tabs: hydrated from server; optimistic on job finish until next fetch. */
  const [unreadTabs, setUnreadTabs] = useState<ReadonlySet<ShotTabJobKey>>(
    () => new Set(seededFromPanel && initialShotRaw ? parseUnreadPreviewTabs(initialShotRaw.unread_preview_tabs) : []),
  )
  const unreadTabsRef = useRef(unreadTabs)
  unreadTabsRef.current = unreadTabs

  const applyShotFromRaw = useCallback(
    (raw: Record<string, unknown>) => {
      const next = mapShotData(raw)
      setShot(next)
      // Active tab: hide dirty locally and bump viewed_at — else reopen lights sticky unread.
      const current = previewTabRef.current
      const serverUnread = next.unread_preview_tabs
      if (
        (current === "storyboard" || current === "video" || current === "shooting_board") &&
        serverUnread.includes(current)
      ) {
        void markShotPreviewTabViewed(projectId, shotId, current)
      }
      setUnreadTabs(new Set(serverUnread.filter((tab) => tab !== current)))
    },
    [projectId, shotId],
  )

  // Persist mark + clear local when an unread preview tab opens (pill or composer sync).
  useEffect(() => {
    if (previewTab !== "storyboard" && previewTab !== "video" && previewTab !== "shooting_board") return
    if (!unreadTabsRef.current.has(previewTab)) return
    setUnreadTabs((prev) => {
      if (!prev.has(previewTab)) return prev
      const next = new Set(prev)
      next.delete(previewTab)
      return next
    })
    void markShotPreviewTabViewed(projectId, shotId, previewTab)
  }, [previewTab, projectId, shotId])

  const markTabUnread = useCallback(
    (tab: ShotTabJobKey) => {
      if (previewTabRef.current === tab) {
        // Saw the new media on this tab — bump viewed_at so reopen/refetch don't light dirty.
        void markShotPreviewTabViewed(projectId, shotId, tab)
        return
      }
      setUnreadTabs((prev) => {
        if (prev.has(tab)) return prev
        const next = new Set(prev)
        next.add(tab)
        return next
      })
    },
    [projectId, shotId],
  )

  const [loading, setLoading] = useState(!seededFromPanel)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [zoomedFrame, setZoomedFrame] = useState<string | null>(null)
  const [previewLightbox, setPreviewLightbox] = useState<"storyboard" | "shooting_board" | null>(null)
  const [sketchPending, setSketchPending] = useState<{
    jobId: number
    target: "storyboard" | "shooting_board"
    createdAt: string
  } | null>(null)
  const [copyingOriginal, setCopyingOriginal] = useState(false)
  const [shootingBoardUploading, setShootingBoardUploading] = useState(false)
  const [shootingBoardUploadError, setShootingBoardUploadError] = useState<string | null>(null)
  const [storyboardUploading, setStoryboardUploading] = useState(false)
  const [storyboardUploadError, setStoryboardUploadError] = useState<string | null>(null)
  const [videoUploading, setVideoUploading] = useState(false)
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null)
  const [animaticStripPanelCount, setAnimaticStripPanelCount] = useState(6)
  const [showAnimaticStripPanel, setShowAnimaticStripPanel] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [animaticLinking, setAnimaticLinking] = useState(false)
  const [postProductionOpen, setPostProductionOpen] = useState(false)
  /** Назначение открытого пикера кадров (null — пикер закрыт). */
  const [frameLibraryTarget, setFrameLibraryTarget] = useState<FrameLibraryTarget | null>(null)
  /**
   * Кадры перехода после ручной правки: ответ эндпоинта обновляет коллаж сразу,
   * не дожидаясь перезагрузки шота (тот же приём, что у раскадровки).
   */
  const [transitionFrames, setTransitionFrames] = useState<TransitionFramesState | null>(null)
  const [transitionBusySide, setTransitionBusySide] = useState<TransitionFrameSide | null>(null)

  const ff = frameFormatClasses(
    effectiveFrameFormat(optimisticOverride(shotFrameFormat, shot?.frame_format), project?.frame_format ?? "9:16"),
  )
  // Same shell as the filled frame so empty ↔ generated don't jump in height.
  // The shooting board sheet is always 16:9; storyboard and video follow the shot.
  const emptyMediaChrome =
    "flex max-h-[min(70vh,600px)] w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-ui-border-strong bg-ui-surface-sunken px-6 text-center text-sm text-ui-text-muted"
  const emptyMediaFrameClass = cn(emptyMediaChrome, ff.aspect)
  const shootingBoardFrameClass = "aspect-video"
  const shootingBoardEmptyClass = cn(emptyMediaChrome, shootingBoardFrameClass)
  const canEditContent = project_permissions?.can_edit_content === true
  const videoCorrectionEnabled = Boolean(project?.effective_video_correction_provider)
  const videoCorrectionAdvanced = Boolean(project?.video_correction_advanced)
  const videoCorrectionTrimMaxSeconds = Number(project?.video_correction_trim_max_seconds) || 15
  const videoCorrectionMarkupMax = Math.max(0, Number(project?.video_correction_markup_max) || 0)
  const storyboardGenLabel = project ? storyboardModelLabel(project) : "—"
  const effectiveModelKey = effectiveShotVideoModelKey(
    optimisticOverride(shotVideoModelKey, shot?.video_model_key),
    project,
  )
  const shotVideoModel = findShotVideoModel(effectiveModelKey, generation_models_by_context?.shot_video)
  const videoGenLabel = shotVideoModel?.label?.trim() || (project ? videoModelLabel(project) : "—")
  const effectiveResolution = effectiveShotVideoResolution(
    optimisticOverride(shotVideoResolution, shot?.video_resolution),
    project,
    shotVideoModel,
  )
  const { durationOptions, currentDuration, adaptiveAllowed, adaptiveActive, videoPrice, supportsMultiShot } =
    useShotVideoGenerationSettings({
      projectId,
      project,
      shotVideoModel,
      shotVideoModels: generation_models_by_context?.shot_video,
      effectiveModelKey,
      effectiveResolution,
      durationTargetSec: shot?.duration_target_sec ?? null,
      hasVideoReferenceInput: shot?.has_video_reference_input ?? false,
      seedanceAdaptiveDuration: Boolean(shot?.seedance_adaptive_duration),
    })
  const durationCoercePatchedRef = useRef<string | null>(null)
  const videoScaleSheetEnsureKeyRef = useRef<string | null>(null)
  const videoPropScaleSheetEnsureKeyRef = useRef<string | null>(null)
  const videoMixedScaleSheetEnsureKeyRef = useRef<string | null>(null)

  const fetchSeqRef = useRef(0)
  const reportInitialLoad = useInitialLoadReporter(onInitialLoadSettled)
  const reportInitialLoadRef = useRef(reportInitialLoad)
  reportInitialLoadRef.current = reportInitialLoad
  /** Skip the first effect run when ShotPanel already seeded us; refreshKey still refetches. */
  const skipNextFetchRef = useRef(
    initialShotRaw != null && typeof initialShotRaw.id === "number" && initialShotRaw.id === shotId,
  )

  const fetchShot = useCallback(() => {
    const seq = ++fetchSeqRef.current
    let cancelled = false
    setLoading(true)
    setFetchError(null)
    fetchShotWorkspaceDetail(projectId, shotId)
      .then(({ shot: raw }) => {
        if (cancelled || seq !== fetchSeqRef.current) return
        applyShotFromRaw(raw as unknown as Record<string, unknown>)
      })
      .catch((err: Error) => {
        if (cancelled || seq !== fetchSeqRef.current) return
        setFetchError(err.message ?? t("preview.loadFailed"))
      })
      .finally(() => {
        if (cancelled || seq !== fetchSeqRef.current) return
        setLoading(false)
        reportInitialLoadRef.current()
      })
    return () => {
      cancelled = true
    }
  }, [projectId, shotId, t, applyShotFromRaw])

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false
      if (initialShotRaw != null && typeof initialShotRaw.id === "number" && initialShotRaw.id === shotId) {
        applyShotFromRaw(initialShotRaw)
        setLoading(false)
        setFetchError(null)
        reportInitialLoadRef.current()
        return
      }
    }
    return fetchShot()
    // initialShotRaw only used on the skipped first run; refreshKey must still refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed is mount-only
  }, [fetchShot, refreshKey])

  useEffect(() => {
    videoScaleSheetEnsureKeyRef.current = null
    videoPropScaleSheetEnsureKeyRef.current = null
    videoMixedScaleSheetEnsureKeyRef.current = null
  }, [shotId])

  useEffect(() => {
    if (previewTab !== "video") {
      videoScaleSheetEnsureKeyRef.current = null
      videoPropScaleSheetEnsureKeyRef.current = null
      videoMixedScaleSheetEnsureKeyRef.current = null
      return
    }
    if (loading || !shot) return

    if (shot.mixed_scale_applicable) {
      if (!shot.video_consider_scale) {
        videoMixedScaleSheetEnsureKeyRef.current = null
        return
      }
      const mixedStatus = shot.mixed_scale_sheet_status
      if (
        mixedStatus === "ready" ||
        mixedStatus === "generating" ||
        mixedStatus === "waiting_pose" ||
        mixedStatus === "waiting_figure" ||
        mixedStatus === "not_applicable" ||
        mixedStatus == null
      ) {
        return
      }
      // Only kick ensure for missing / stale (and any other actionable status).
      if (mixedStatus !== "missing" && mixedStatus !== "stale") return

      const dedupeKey = `${shotId}:video-mixed:${mixedStatus}:${shot.video_consider_scale}`
      if (videoMixedScaleSheetEnsureKeyRef.current === dedupeKey) return
      videoMixedScaleSheetEnsureKeyRef.current = dedupeKey

      void ensureShotMixedScaleSheet(projectId, shotId)
        .then((result) => {
          if (result.status === "generating") {
            onDataChanged()
            fetchShot()
            return
          }
          if (
            result.status === "not_applicable" ||
            result.status === "waiting_pose" ||
            result.status === "waiting_figure"
          ) {
            videoMixedScaleSheetEnsureKeyRef.current = null
          }
        })
        .catch(() => {
          videoMixedScaleSheetEnsureKeyRef.current = null
        })
      return
    }

    if (!shot.scale_applicable || !shot.video_consider_scale) return
    if (shot.scale_sheet_status === "ready" || shot.scale_sheet_status === "generating") return

    const dedupeKey = `${shotId}:video`
    if (videoScaleSheetEnsureKeyRef.current === dedupeKey) return
    videoScaleSheetEnsureKeyRef.current = dedupeKey

    void ensureShotScaleSheet(projectId, shotId)
      .then((result) => {
        if (result.status === "generating") {
          onDataChanged()
          fetchShot()
        }
      })
      .catch(() => {
        videoScaleSheetEnsureKeyRef.current = null
      })
  }, [previewTab, loading, shot, shotId, projectId, onDataChanged, fetchShot])

  useEffect(() => {
    if (previewTab !== "video") return
    if (loading || !shot) return
    if (shot.mixed_scale_applicable) return
    if (!shot.prop_scale_applicable || !shot.video_consider_prop_scale) return
    if (shot.prop_scale_sheet_status === "ready" || shot.prop_scale_sheet_status === "generating") return

    const dedupeKey = `${shotId}:video-prop`
    if (videoPropScaleSheetEnsureKeyRef.current === dedupeKey) return
    videoPropScaleSheetEnsureKeyRef.current = dedupeKey

    void ensureShotPropScaleSheet(projectId, shotId)
      .then((result) => {
        if (result.status === "generating") {
          onDataChanged()
          fetchShot()
        }
      })
      .catch(() => {
        videoPropScaleSheetEnsureKeyRef.current = null
      })
  }, [previewTab, loading, shot, shotId, projectId, onDataChanged, fetchShot])

  const activeVideoJob = useMemo(() => activeShotJob(jobs, shotId, TAB_JOB_KINDS.video), [jobs, shotId])
  // Lipsync-джобы держим отдельно от TAB_JOB_KINDS.video: они замораживают только
  // свои клипы в редакторе и не должны блокировать генерацию видео целиком.
  const activeLipsyncJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          (j.status === "queued" || j.status === "processing") &&
          j.job_kind === "shot_timeline_lipsync" &&
          (j.shot_ids ?? []).includes(shotId),
      ),
    [jobs, shotId],
  )
  // Correction jobs that target a timeline clip — freeze that clip in the editor.
  const activeCorrectionJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          (j.status === "queued" || j.status === "processing") &&
          (j.job_kind === "kling_video_correction" || j.job_kind === "byteplus_seedance_video_correction") &&
          Boolean(j.correction_clip_id) &&
          (j.shot_ids ?? []).includes(shotId),
      ),
    [jobs, shotId],
  )
  const activeLipsyncJob = activeLipsyncJobs[0] ?? null
  const activeStoryboardJob = useMemo(() => activeShotJob(jobs, shotId, TAB_JOB_KINDS.storyboard), [jobs, shotId])
  const activeShootingBoardJob = useMemo(
    () => activeShotJob(jobs, shotId, TAB_JOB_KINDS.shooting_board),
    [jobs, shotId],
  )
  const sketchPendingJob = useMemo(() => {
    if (!sketchPending) return null
    const job = jobs.find((j) => j.id === sketchPending.jobId)
    if (!job || (job.status !== "queued" && job.status !== "processing")) return null
    return job
  }, [jobs, sketchPending])
  const recoveredStoryboardSketchJob = useMemo(
    () => inFlightSketchEditForShot(jobs, shotId, "storyboard"),
    [jobs, shotId],
  )
  const recoveredShootingBoardSketchJob = useMemo(
    () => inFlightSketchEditForShot(jobs, shotId, "shooting_board"),
    [jobs, shotId],
  )
  const storyboardSketchJob = useMemo(() => {
    if (sketchPending?.target === "storyboard") {
      return (
        sketchPendingJob ??
        recoveredStoryboardSketchJob ?? {
          id: sketchPending.jobId,
          job_kind: "sketch_edit",
          status: "queued",
          progress: 0,
          error_message: null,
          created_at: sketchPending.createdAt,
        }
      )
    }
    return recoveredStoryboardSketchJob
  }, [sketchPending, sketchPendingJob, recoveredStoryboardSketchJob])
  const shootingBoardSketchJob = useMemo(() => {
    if (sketchPending?.target === "shooting_board") {
      return (
        sketchPendingJob ??
        recoveredShootingBoardSketchJob ?? {
          id: sketchPending.jobId,
          job_kind: "sketch_edit",
          status: "queued",
          progress: 0,
          error_message: null,
          created_at: sketchPending.createdAt,
        }
      )
    }
    return recoveredShootingBoardSketchJob
  }, [sketchPending, sketchPendingJob, recoveredShootingBoardSketchJob])
  const sketchCorrectionTitle = t("canvas.sketch.aiHeading", { ns: "workspace" })
  const storyboardActionsJob = storyboardSketchJob ?? activeStoryboardJob
  const storyboardActionsTitle = storyboardSketchJob ? sketchCorrectionTitle : undefined
  const shootingBoardActionsJob = shootingBoardSketchJob ?? activeShootingBoardJob
  const shootingBoardActionsTitle = shootingBoardSketchJob ? sketchCorrectionTitle : undefined

  const handleJobFinished = useCallback(() => {
    onDataChanged()
    fetchShot()
  }, [onDataChanged, fetchShot])

  const handleStoryboardJobFinished = useCallback(
    (finishedJobId: number) => {
      handleJobFinished()
      if (jobStatusMarksTabUnread(jobs.find((j) => j.id === finishedJobId)?.status)) {
        markTabUnread("storyboard")
      }
    },
    [handleJobFinished, markTabUnread, jobs],
  )

  const handleVideoJobFinished = useCallback(
    (finishedJobId: number) => {
      handleJobFinished()
      if (jobStatusMarksTabUnread(jobs.find((j) => j.id === finishedJobId)?.status)) {
        markTabUnread("video")
      }
    },
    [handleJobFinished, markTabUnread, jobs],
  )

  const handleShootingBoardJobFinished = useCallback(
    (finishedJobId: number) => {
      handleJobFinished()
      if (jobStatusMarksTabUnread(jobs.find((j) => j.id === finishedJobId)?.status)) {
        markTabUnread("shooting_board")
      }
    },
    [handleJobFinished, markTabUnread, jobs],
  )

  const handlePostProductionSubmitted = useCallback(() => {
    onDataChanged()
    fetchShot()
  }, [onDataChanged, fetchShot])

  useReloadOnJobFinish(activeVideoJob, handleVideoJobFinished)
  useReloadOnJobFinish(activeStoryboardJob, handleStoryboardJobFinished)
  useReloadOnJobFinish(activeShootingBoardJob, handleShootingBoardJobFinished)
  useReloadOnJobFinish(activeLipsyncJob, handleVideoJobFinished)
  useReloadOnJobFinish(storyboardSketchJob, handleStoryboardJobFinished)
  useReloadOnJobFinish(shootingBoardSketchJob, handleShootingBoardJobFinished)

  useEffect(() => {
    if (!sketchPending) return
    const status = jobs.find((j) => j.id === sketchPending.jobId)?.status ?? null
    if (status === "completed" || status === "failed" || status === "cancelled") {
      setSketchPending(null)
    }
  }, [jobs, sketchPending])

  const handleStoryboardSketchPending = useCallback(
    ({ jobId, active }: SketchCorrectionPendingChange) => {
      if (active) {
        setSketchPending((current) => {
          if (current?.jobId === jobId && current.target === "storyboard") return current
          return {
            jobId,
            target: "storyboard",
            createdAt: current?.target === "storyboard" ? current.createdAt : new Date().toISOString(),
          }
        })
        if (jobId > 0) onDataChanged()
        return
      }
      setSketchPending((current) => (current?.jobId === jobId ? null : current))
    },
    [onDataChanged],
  )

  const handleShootingBoardSketchPending = useCallback(
    ({ jobId, active }: SketchCorrectionPendingChange) => {
      if (active) {
        setSketchPending((current) => {
          if (current?.jobId === jobId && current.target === "shooting_board") return current
          return {
            jobId,
            target: "shooting_board",
            createdAt: current?.target === "shooting_board" ? current.createdAt : new Date().toISOString(),
          }
        })
        if (jobId > 0) onDataChanged()
        return
      }
      setSketchPending((current) => (current?.jobId === jobId ? null : current))
    },
    [onDataChanged],
  )

  const handleDownloadOriginal = useCallback((url: string | null | undefined, filename: string) => {
    if (url) void downloadImage(url, filename)
  }, [])

  const handleCopyOriginal = useCallback(
    async (url: string | null | undefined) => {
      if (!url || copyingOriginal) return
      setCopyingOriginal(true)
      try {
        await copyImageToClipboard(url)
        toast.success(t("common.originalCopied"))
      } catch {
        toast.error(t("common.copyImageFailed"))
      } finally {
        setCopyingOriginal(false)
      }
    },
    [copyingOriginal, t],
  )

  const handleSelectDuration = useCallback(
    (dur: number) => {
      if (!shot) return
      const patch = nextShotDurationPatch({
        selected: dur,
        currentDurationSec: shot.duration_target_sec ?? null,
        adaptiveActive: Boolean(shot.seedance_adaptive_duration),
      })
      if (!patch) return
      patchShotVideoSettings(projectId, shot.id, patch)
      setShot((prev) => {
        if (!prev) return prev
        if (dur === ADAPTIVE_DURATION_SEC) return { ...prev, seedance_adaptive_duration: true }
        return { ...prev, duration_target_sec: dur, seedance_adaptive_duration: false }
      })
    },
    [projectId, shot],
  )

  useEffect(() => {
    if (!shot || durationOptions.length === 0) return
    const raw = shot.duration_target_sec
    const coerced = nearestDurationSec(raw, durationOptions)
    if (coerced == null || raw === coerced) return
    const coerceKey = `${shot.id}:${durationOptions.join(",")}`
    if (durationCoercePatchedRef.current === coerceKey) return
    durationCoercePatchedRef.current = coerceKey
    patchShotVideoSettings(projectId, shot.id, { duration_target_sec: coerced })
    setShot((prev) => (prev ? { ...prev, duration_target_sec: coerced } : prev))
  }, [shot, durationOptions, projectId])

  const handleToggleMultiShot = useCallback(
    (multi: boolean) => {
      if (!shot) return
      const next = multi ? "multi" : "single"
      patchShotVideoSettings(projectId, shot.id, { video_shot_type: next })
      setShot((prev) => (prev ? { ...prev, video_shot_type: next } : prev))
    },
    [projectId, shot],
  )

  useEffect(() => {
    setTransitionFrames(null)
    setAnimaticStripPanelCount(6)
    setShowAnimaticStripPanel(false)
    setShootingBoardUploadError(null)
    setStoryboardUploadError(null)
    setVideoUploadError(null)
    setZoomedFrame(null)
    setAnimaticLinking(false)
    durationCoercePatchedRef.current = null
    setFirstGenResolutionOpen(false)
  }, [shotId])

  const hasVideo = Boolean(shot?.video_clip_url)
  const hasStoryboard = Boolean(shot?.storyboard_preview_url?.trim())
  const shootingBoardPreviewUrlEffective =
    shot?.shooting_board_pane_url?.trim() || shot?.shooting_board_preview_url?.trim() || ""
  const hasShootingBoard = Boolean(shootingBoardPreviewUrlEffective)
  const isTransition = shot?.shot_kind === "transition"
  const showShootingBoardTab = !isTransition
  const hasAnimaticSegment = Boolean(shot?.has_animatic_segment)
  const shotNeedsReferenceFrameWarning = Boolean(shot && !isTransition && !shot.storyboard_preview_url?.trim())

  const handleGenerateStoryboard = useCallback(() => {
    if (!shot || busy) return
    setBusy("generateStoryboard")
    runWorkspace(
      projectId,
      "generate_storyboard",
      { shot_ids: [shot.id] },
      {
        onFinish: () => {
          setBusy(null)
          onDataChanged()
        },
      },
    )
  }, [projectId, shot, busy, onDataChanged])

  const runGenerateVideoRequest = useCallback(
    async (videoResolutionOverride: string | null): Promise<boolean> => {
      if (!shot || busy) return false
      if (beforeGenerateVideo && !(await beforeGenerateVideo())) {
        toast.error(t("common.saveErrorRetry"))
        return false
      }
      let started = false
      await requestSeedanceVideoGeneration({
        shotIds: [shot.id],
        videoResolutionOverride,
        onStart: () => {
          started = true
          setBusy("generateVideo")
        },
        visitOptions: {
          onFinish: () => {
            setBusy(null)
            onDataChanged()
          },
        },
      })
      return started
    },
    [shot, busy, beforeGenerateVideo, requestSeedanceVideoGeneration, onDataChanged, t],
  )

  const startGenerateVideo = useCallback(async (): Promise<boolean> => {
    if (!shot || busy) return false
    return runGenerateVideoRequest(null)
  }, [shot, busy, runGenerateVideoRequest])

  const { beforeArm: beforeArmGenerateVideo, minOption: firstGenMinOption } = useFirstGenResolutionGate({
    needsReferenceFrame: shotNeedsReferenceFrameWarning,
    hasVideo: Boolean(shot?.video_clip_url),
    effectiveResolution,
    model: shotVideoModel,
    setDialogOpen: setFirstGenResolutionOpen,
    beforeResolution: confirmMotionReferences,
  })

  const handleExtractFrames = useCallback(() => {
    if (!shot || busy) return
    setBusy("extractFrames")
    runWorkspace(
      projectId,
      "extract_video_frames",
      { shot_id: shot.id },
      {
        onFinish: () => {
          setBusy(null)
          onDataChanged()
        },
      },
    )
  }, [projectId, shot, busy, onDataChanged])

  const handleGenerateShootingBoard = useCallback(() => {
    if (!shot || busy) return
    setBusy("generateShootingBoard")
    runWorkspace(
      projectId,
      "generate_shooting_board",
      { shot_ids: [shot.id] },
      {
        onFinish: () => {
          setBusy(null)
          onDataChanged()
        },
      },
    )
  }, [projectId, shot, busy, onDataChanged])

  const handleBuildShootingBoardFromAnimatic = useCallback(() => {
    if (!shot || !hasAnimaticSegment || busy) return
    setBusy("buildAnimaticStrip")
    runWorkspace(
      projectId,
      "build_animatic_shooting_board_strip",
      {
        shot_id: shot.id,
        panel_count: animaticStripPanelCount,
      },
      {
        onFinish: () => {
          setBusy(null)
          onDataChanged()
          fetchShot()
        },
      },
    )
  }, [projectId, shot, hasAnimaticSegment, animaticStripPanelCount, busy, onDataChanged, fetchShot])

  const handleShootingBoardFile = useCallback(
    async (file: File) => {
      if (!shot) return
      setShootingBoardUploading(true)
      setShootingBoardUploadError(null)
      try {
        await uploadShotShootingBoard(projectId, shot.id, file)
        onDataChanged()
        fetchShot()
      } catch (e) {
        setShootingBoardUploadError(e instanceof Error ? e.message : t("preview.uploadShootingBoardFailed"))
      } finally {
        setShootingBoardUploading(false)
        if (shootingBoardFileRef.current) shootingBoardFileRef.current.value = ""
      }
    },
    [projectId, shot, onDataChanged, fetchShot, t],
  )

  const handleStoryboardFile = useCallback(
    async (file: File) => {
      if (!shot) return
      setStoryboardUploading(true)
      setStoryboardUploadError(null)
      try {
        await uploadShotStoryboard(projectId, shot.id, file)
        onDataChanged()
        fetchShot()
      } catch (e) {
        setStoryboardUploadError(e instanceof Error ? e.message : t("preview.uploadFrameFailed"))
      } finally {
        setStoryboardUploading(false)
        if (storyboardFileRef.current) storyboardFileRef.current.value = ""
      }
    },
    [projectId, shot, onDataChanged, fetchShot, t],
  )

  const handleConfirmShootingBoardSketch = useCallback(
    async (file: File) => {
      if (!shot) return
      await uploadShotShootingBoard(projectId, shot.id, file)
      onDataChanged()
      fetchShot()
    },
    [projectId, shot, onDataChanged, fetchShot],
  )

  const handleConfirmStoryboardSketch = useCallback(
    async (file: File) => {
      if (!shot) return
      await uploadShotStoryboard(projectId, shot.id, file)
      onDataChanged()
      fetchShot()
    },
    [projectId, shot, onDataChanged, fetchShot],
  )

  const sketchEdit = useMemo(
    () => ({
      available: Boolean(sketch?.available),
      modelLabel: sketch?.modelLabel ?? "",
      modelKey: sketch?.modelKey,
    }),
    [sketch],
  )

  // Full original same-origin proxy only — never display preview (≤800, blurry in sketch).
  const storyboardSketchUrl = shot?.storyboard_download_url?.trim() || null
  const shootingBoardSketchUrl = shot?.shooting_board_download_url?.trim() || null

  const storyboardBrowserUrl = shot?.storyboard_download_url?.trim() || null
  const shootingBoardBrowserUrl = shot?.shooting_board_download_url?.trim() || null

  const handleStoryboardFromPrevShot = useCallback(async () => {
    if (!shot?.prev_shot_last_frame || busy) return
    const csrf = getCsrfToken()
    setBusy("storyboardFromPrev")
    setStoryboardUploadError(null)
    try {
      const res = await fetch(`/projects/${projectId}/workspace/shots/${shot.id}/storyboard_from_prev_last_frame`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrf,
        },
        credentials: "same-origin",
        body: JSON.stringify({ authenticity_token: csrf }),
      })
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
      if (!res.ok || !data.ok) {
        setStoryboardUploadError(data.error ?? t("preview.storyboardFromPrevShotFailed"))
        return
      }
      onDataChanged()
      fetchShot()
    } catch {
      setStoryboardUploadError(t("common.networkUnavailable"))
    } finally {
      setBusy(null)
    }
  }, [projectId, shot, busy, onDataChanged, fetchShot, t])

  // Опорник из выбранного кадра библиотеки (пикер поверх панели): ошибки — тостом,
  // пикер остаётся открытым, чтобы можно было выбрать другой кадр.
  const handleStoryboardFromSource = useCallback(
    async (source: ShotFrameSource) => {
      if (!shot || busy) return
      setBusy("storyboardFromSource")
      try {
        await setShotStoryboardFromSource(projectId, shot.id, source)
        setFrameLibraryTarget(null)
        onDataChanged()
        fetchShot()
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("preview.storyboardFromLibraryFailed"))
      } finally {
        setBusy(null)
      }
    },
    [projectId, shot, busy, onDataChanged, fetchShot, t],
  )

  // Раскадровка из кадра библиотеки. Картинку обновляет fetchShot (pane URL).
  const handleShootingBoardFromSource = useCallback(
    async (source: ShotFrameSource) => {
      if (!shot || busy) return
      setBusy("shootingBoardFromSource")
      try {
        await setShotShootingBoardFromSource(projectId, shot.id, source)
        setFrameLibraryTarget(null)
        onDataChanged()
        fetchShot()
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("preview.shootingBoardFromLibraryFailed"))
      } finally {
        setBusy(null)
      }
    },
    [projectId, shot, busy, onDataChanged, fetchShot, t],
  )

  // Клип шота из видео библиотеки: тот же UX, что у опорника / раскадровки.
  const handleVideoFromSource = useCallback(
    async (source: ShotFrameSource) => {
      if (!shot || busy) return
      setBusy("videoFromSource")
      try {
        await setShotVideoFromSource(projectId, shot.id, source)
        setFrameLibraryTarget(null)
        onDataChanged()
        fetchShot()
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("preview.videoFromLibraryFailed"))
      } finally {
        setBusy(null)
      }
    },
    [projectId, shot, busy, onDataChanged, fetchShot, t],
  )

  // Кадр половины перехода из библиотеки. Ответ несёт обе стороны сразу — сервер мог
  // пересчитать соседнюю (например, снялся wrap на краю эпизода).
  const handleTransitionFrameFromSource = useCallback(
    async (side: TransitionFrameSide, source: ShotFrameSource) => {
      if (!shot || transitionBusySide) return
      setTransitionBusySide(side)
      try {
        setTransitionFrames(await setShotTransitionFrameFromSource(projectId, shot.id, side, source))
        setFrameLibraryTarget(null)
        onDataChanged()
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("transitionFrames.assignFailed"))
      } finally {
        setTransitionBusySide(null)
      }
    },
    [projectId, shot, transitionBusySide, onDataChanged, t],
  )

  const handleResetTransitionFrame = useCallback(
    async (side: TransitionFrameSide) => {
      if (!shot || transitionBusySide) return
      setTransitionBusySide(side)
      try {
        setTransitionFrames(await resetShotTransitionFrame(projectId, shot.id, side))
        onDataChanged()
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("transitionFrames.resetFailed"))
      } finally {
        setTransitionBusySide(null)
      }
    },
    [projectId, shot, transitionBusySide, onDataChanged, t],
  )

  const handleVideoFile = useCallback(
    async (file: File) => {
      if (!shot) return
      const csrf = getCsrfToken()
      setVideoUploading(true)
      setVideoUploadError(null)
      try {
        const form = new FormData()
        form.append("file", file)
        form.append("authenticity_token", csrf)
        const res = await fetch(`/projects/${projectId}/workspace/shots/${shot.id}/video_upload`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": csrf,
          },
          credentials: "same-origin",
          body: form,
        })
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
        if (!res.ok || !data.ok) {
          setVideoUploadError(data.error ?? t("preview.uploadVideoFailed"))
          return
        }
        onDataChanged()
        fetchShot()
      } catch {
        setVideoUploadError(t("common.networkUnavailable"))
      } finally {
        setVideoUploading(false)
        if (videoFileRef.current) videoFileRef.current.value = ""
      }
    },
    [projectId, shot, onDataChanged, fetchShot, t],
  )

  // Загрузка аниматика прямо в шоте. Файл живёт вне ленты серии и сразу
  // привязывается к шоту; сегмент появляется не мгновенно — его создаёт
  // ffprobe-джоба, поэтому после заливки поллим шот, пока он не доедет.
  const animaticFileRef = useRef<HTMLInputElement>(null)
  const [animaticUploadPercent, setAnimaticUploadPercent] = useState<number | null>(null)
  const [animaticUploadError, setAnimaticUploadError] = useState<string | null>(null)
  const [awaitingAnimaticSourceId, setAwaitingAnimaticSourceId] = useState<number | null>(null)
  const awaitingAnimatic = awaitingAnimaticSourceId != null

  const handleUploadAnimatic = useCallback(
    async (file: File) => {
      if (!shot || animaticUploadPercent !== null) return

      if (shot.animatic_segment) {
        const ok = await confirm({
          title: t("common.replaceAnimaticTitle"),
          message: t("common.replaceAnimaticMessage"),
          confirmLabel: t("common.replaceAnimaticConfirm"),
        })
        if (!ok) return
      }

      setAnimaticUploadError(null)
      setAnimaticUploadPercent(0)
      try {
        const result = await uploadShotAnimatic(projectId, shot.id, file, setAnimaticUploadPercent)
        onDataChanged()
        fetchShot()
        // Сегмент создаёт ffprobe-джоба, а не сам аплоад: без ожидания вкладка
        // сразу после загрузки показала бы пустое состояние.
        // Ждём именно залитый source_id: при замене старый сегмент ещё висит,
        // пока ExtractMetadata не перепривяжет шот.
        setAwaitingAnimaticSourceId(result.source_id ?? null)
      } catch (e) {
        setAnimaticUploadError(e instanceof Error ? e.message : String(e))
      } finally {
        setAnimaticUploadPercent(null)
      }
    },
    [projectId, shot, animaticUploadPercent, onDataChanged, fetchShot, t],
  )

  // Видео из пикера библиотеки: сервер сам решает, привязать сегмент или завести
  // шотовый ролик из файла. Прежний сегмент только отцепляется — файлы остаются.
  const handleAnimaticFromLibrary = useCallback(
    async (source: ShotFrameSource) => {
      if (!shot || animaticLinking) return
      if (shot.animatic_segment) {
        const ok = await confirm({
          title: t("common.replaceAnimaticTitle"),
          message: t("common.replaceAnimaticMessage"),
          confirmLabel: t("common.replaceAnimaticConfirm"),
        })
        if (!ok) return
      }
      setAnimaticLinking(true)
      try {
        const result = await setShotAnimaticFromSource(projectId, shot.id, source)
        setFrameLibraryTarget(null)
        onDataChanged()
        fetchShot()
        if ("source_id" in result) setAwaitingAnimaticSourceId(result.source_id)
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("common.linkSegmentFailed"))
      } finally {
        setAnimaticLinking(false)
      }
    },
    [projectId, shot, animaticLinking, onDataChanged, fetchShot, t],
  )

  // Ждём, пока привязанный сегмент доедет И ExtractTimeline закончит нарезку.
  // Кадры режутся в том же джобе ПОСЛЕ link_shot: сегмент виден раньше покадровки.
  // timeline_frames_status (ready/skipped) — терминальный сигнал, в том числе когда
  // кадров не будет; длина URL — запас на переходный деплой без поля.
  // Ограничение по попыткам — чтобы зависшая джоба не оставила бесконечный опрос.
  const segSourceId = shot?.animatic_segment?.animatic_source_id ?? null
  const segFrameCount = shot?.animatic_segment?.timeline_frame_urls.length ?? 0
  const segFramesStatus = shot?.animatic_segment?.timeline_frames_status ?? null

  useEffect(() => {
    if (awaitingAnimaticSourceId == null) return
    const sourceReady = segSourceId === awaitingAnimaticSourceId
    const framesReady = segFrameCount > 0
    const framesSettled = segFramesStatus === "skipped" || segFramesStatus === "ready"
    if (sourceReady && (framesReady || framesSettled)) {
      setAwaitingAnimaticSourceId(null)
      onDataChanged()
      return
    }
    let attempts = 0
    const timer = setInterval(() => {
      attempts += 1
      if (attempts > 45) {
        clearInterval(timer)
        setAwaitingAnimaticSourceId(null)
        onDataChanged()
        return
      }
      fetchShot()
    }, 2000)
    return () => clearInterval(timer)
  }, [awaitingAnimaticSourceId, segSourceId, segFrameCount, segFramesStatus, fetchShot, onDataChanged])

  // Ключ уже привязанного сегмента — гасит его плитку в пикере библиотеки.
  const animaticPickedKeys = useMemo<readonly string[]>(
    () => (shot?.animatic_segment ? [librarySourceKey(animaticSegmentSource(shot.animatic_segment.id))] : []),
    [shot?.animatic_segment],
  )

  // Открепление — и для сегмента ленты, и для своего ролика: файл не удаляется,
  // откреплённый ролик остаётся в библиотеке в папке шота (история версий).
  const handleUnlinkSegment = useCallback(async () => {
    if (!shot?.animatic_segment || animaticLinking) return
    setAnimaticLinking(true)
    try {
      await unlinkAnimaticSegment(projectId, shot.animatic_segment.id)
      onDataChanged()
      fetchShot()
    } finally {
      setAnimaticLinking(false)
    }
  }, [projectId, shot, animaticLinking, onDataChanged, fetchShot])

  // ── Превиз шота: загрузка, выбор из библиотеки, отвязка ──
  // Как у аниматика: у шота один привязанный превиз, замена только отвязывает
  // прежний (файл остаётся в библиотеке превизов).
  const previzFileRef = useRef<HTMLInputElement>(null)
  const [previzBusy, setPrevizBusy] = useState(false)
  const [previzError, setPrevizError] = useState<string | null>(null)
  const [previzRecordOpen, setPrevizRecordOpen] = useState(false)
  const [previzCreateOpen, setPrevizCreateOpen] = useState(false)
  const [captureHints, setCaptureHints] = useState(false)
  const [scenePickerOpen, setScenePickerOpen] = useState(false)
  const [recentScenes, setRecentScenes] = useState<LibraryScene3dItem[]>([])
  const [previzRecordScene3dId, setPrevizRecordScene3dId] = useState<number | null>(null)
  const [previzRecordPrevizId, setPrevizRecordPrevizId] = useState<number | null>(null)
  const [previzRecordInitialState, setPrevizRecordInitialState] = useState<LibraryPrevizItem["scene_state"] | null>(
    null,
  )
  const [previzRecordSceneName, setPrevizRecordSceneName] = useState("")
  const onScene3dSessionChangeRef = useRef(onScene3dSessionChange)
  onScene3dSessionChangeRef.current = onScene3dSessionChange

  useEffect(() => {
    onScene3dSessionChangeRef.current?.({
      open: previzRecordOpen,
      scene3dId: previzRecordScene3dId,
      name: previzRecordSceneName,
    })
  }, [previzRecordOpen, previzRecordScene3dId, previzRecordSceneName])

  useEffect(() => {
    return () => {
      onScene3dSessionChangeRef.current?.({ open: false, scene3dId: null, name: "" })
    }
  }, [])

  const confirmReplacePreviz = useCallback(async () => {
    if (!shot?.previz) return true
    return confirm({
      title: t("previz.replaceTitle"),
      message: t("previz.replaceMessage"),
      confirmLabel: t("previz.replaceConfirm"),
    })
  }, [shot?.previz, t])

  const openLinkedScene = useCallback(async () => {
    const existing = shot?.previz
    if (!existing || existing.scene3d_id == null || previzBusy) return
    setPrevizBusy(true)
    try {
      const detail = await fetchScene3d(projectId, existing.scene3d_id)
      setPrevizRecordScene3dId(detail.id)
      setPrevizRecordSceneName(detail.name)
      setPrevizRecordPrevizId(existing.id)
      setPrevizRecordInitialState(detail.scene_state ?? existing.scene_state ?? null)
      setCaptureHints(false)
      setPrevizRecordOpen(true)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
    } finally {
      setPrevizBusy(false)
    }
  }, [shot, previzBusy, projectId])

  const handleRecordPreviz = useCallback(async () => {
    if (!shot || previzBusy) return
    if (!(await confirmReplacePreviz())) return

    if (shot.previz?.scene3d_id != null) {
      await openLinkedScene()
      return
    }

    setPrevizCreateOpen(true)
  }, [shot, previzBusy, confirmReplacePreviz, openLinkedScene])

  const openSceneForRecord = useCallback(
    async (sceneId: number) => {
      if (!shot || previzBusy) return
      setScenePickerOpen(false)
      setPrevizBusy(true)
      try {
        const detail = await fetchScene3d(projectId, sceneId)
        setPrevizRecordScene3dId(detail.id)
        setPrevizRecordSceneName(detail.name)
        setPrevizRecordPrevizId(null)
        setPrevizRecordInitialState(detail.scene_state ?? null)
        setCaptureHints(false)
        setPrevizRecordOpen(true)
      } catch (e) {
        toast.error(e instanceof Error ? e.message : String(e))
      } finally {
        setPrevizBusy(false)
      }
    },
    [shot, previzBusy, projectId],
  )

  useEffect(() => {
    if (previewTab !== "previz" || shot?.previz) return
    let cancelled = false
    void fetchLibraryScene3ds(projectId, { limit: 20 })
      .then((page) => {
        if (cancelled) return
        setRecentScenes(pickRecentScenes(page.scene3ds, shot?.location_id ?? null))
      })
      .catch(() => {
        if (!cancelled) setRecentScenes([])
      })
    return () => {
      cancelled = true
    }
  }, [previewTab, projectId, shot?.previz, shot?.location_id])

  const handleUploadPreviz = useCallback(
    async (file: File) => {
      if (!shot || previzBusy) return
      if (!(await confirmReplacePreviz())) return

      setPrevizError(null)
      setPrevizBusy(true)
      try {
        const name = file.name.replace(/\.[^.]+$/, "").trim() || `previz_shot_${shot.number}`
        await createPreviz(projectId, {
          file,
          name,
          shotId: shot.id,
          locationId: shot.location_id ?? undefined,
          scene3dId: shot.previz?.scene3d_id ?? undefined,
        })
        onDataChanged()
        fetchShot()
      } catch (e) {
        setPrevizError(e instanceof Error ? e.message : String(e))
      } finally {
        setPrevizBusy(false)
      }
    },
    [projectId, shot, previzBusy, confirmReplacePreviz, onDataChanged, fetchShot],
  )

  const handlePrevizFromLibrary = useCallback(
    async (source: ShotFrameSource) => {
      if (!shot || previzBusy) return
      if (!(await confirmReplacePreviz())) return
      setPrevizBusy(true)
      try {
        await createPrevizFromSource(projectId, shot.id, source)
        setFrameLibraryTarget(null)
        onDataChanged()
        fetchShot()
      } catch (err) {
        toast.error(err instanceof Error && err.message ? err.message : t("previz.pickFailed"))
      } finally {
        setPrevizBusy(false)
      }
    },
    [projectId, shot, previzBusy, confirmReplacePreviz, onDataChanged, fetchShot, t],
  )

  const handleUnlinkPreviz = useCallback(async () => {
    if (!shot?.previz || previzBusy) return
    setPrevizBusy(true)
    try {
      await linkPrevizToShot(projectId, shot.previz.id, null)
      onDataChanged()
      fetchShot()
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : t("previz.linkFailed"))
    } finally {
      setPrevizBusy(false)
    }
  }, [projectId, shot, previzBusy, onDataChanged, fetchShot, t])

  const previzPickedKeys = useMemo<readonly string[]>(
    () => (shot?.previz ? [librarySourceKey(previzSource(shot.previz.id))] : []),
    [shot?.previz],
  )

  // Транскод webm→mp4 идёт в фоне: пока вкладка открыта и статус не терминальный,
  // мягко поллим шот, чтобы плеер сам переключился на готовый mp4.
  const previzStatus = shot?.previz?.status ?? null
  const previzId = shot?.previz?.id ?? null
  // Локальный флаг с onCaptured: upload ещё не вернул новый превиз, старый status ready не должен гасить плашку.
  const [previzPrep, setPrevizPrep] = useState<{ startedAt: string; baselineId: number | null } | null>(null)
  useEffect(() => {
    setPrevizPrep(null)
  }, [shotId])
  useEffect(() => {
    if (!previzPrep) return
    if (previzId == null || previzId === previzPrep.baselineId) return
    if (previzStatus === "ready" || previzStatus === "failed") setPrevizPrep(null)
  }, [previzPrep, previzId, previzStatus])
  const [previzMedia, setPrevizMedia] = useState<"clip" | "depth">("clip")
  const [depthGenerateBusy, setDepthGenerateBusy] = useState(false)
  const depthHydratedShotId = useRef<number | null>(null)
  useEffect(() => {
    setPrevizMedia("clip")
    setDepthGenerateBusy(false)
    depthHydratedShotId.current = null
  }, [shotId])
  useEffect(() => {
    if (!shot || depthHydratedShotId.current === shot.id) return
    depthHydratedShotId.current = shot.id
    setPrevizDepthVariant(shot.previz_depth_variant)
  }, [shot, setPrevizDepthVariant])
  const depthStatus = shot?.previz?.depth_status ?? null
  const depthInFlight = depthStatus === "pending" || depthStatus === "processing" || depthGenerateBusy
  useEffect(() => {
    if (previewTab !== "previz") return
    const transcoding = previzStatus === "pending" || previzStatus === "processing"
    if (!transcoding && !depthInFlight) return
    const timer = setInterval(() => fetchShot(), 3000)
    return () => clearInterval(timer)
  }, [previewTab, previzStatus, depthInFlight, fetchShot])

  // Карточки «Отправить превиз» живут в колонке промпта и читают previz оттуда.
  // Сообщаем ей, когда у того же превиза маска появилась, считается или устарела.
  const depthNotifyKeyRef = useRef<string | null>(null)
  const depthNotifyKey = shot?.previz
    ? `${shot.previz.id}:${shot.previz.depth_status ?? ""}:${shot.previz.depth_current ? 1 : 0}`
    : null
  useEffect(() => {
    if (depthNotifyKey == null) return
    const previous = depthNotifyKeyRef.current
    depthNotifyKeyRef.current = depthNotifyKey
    if (previous == null || previous === depthNotifyKey) return
    const previousId = previous.slice(0, previous.indexOf(":"))
    const nextId = depthNotifyKey.slice(0, depthNotifyKey.indexOf(":"))
    if (previousId !== nextId) return
    onDataChanged()
  }, [depthNotifyKey, onDataChanged])

  const handlePrevizDepthVariant = useCallback(
    (variant: PrevizDepthVariant) => {
      setPrevizDepthVariant(variant)
      if (!shot) return
      void patchShotPromptSettings(projectId, shot.id, { seedance_previz_depth_variant: variant }).catch(
        (e: unknown) => {
          toast.error(e instanceof Error ? e.message : t("common.loadSettingsError"))
        },
      )
    },
    [projectId, setPrevizDepthVariant, shot, t],
  )

  const handleGeneratePrevizDepth = useCallback(() => {
    if (!shot?.previz || depthGenerateBusy) return
    setDepthGenerateBusy(true)
    void generatePrevizDepth(projectId, shot.previz.id)
      .then(() => {
        fetchShot()
      })
      .catch((e: unknown) => {
        toast.error(e instanceof Error ? e.message : t("previz.depthGenerateFailed"))
      })
      .finally(() => {
        setDepthGenerateBusy(false)
      })
  }, [depthGenerateBusy, fetchShot, projectId, shot, t])

  // Каст и пропсы для 3D-сцены — лениво при первом открытии записи превиза:
  // панель шота их не грузит, а сцене нужен весь каталог для кнопок «добавить».
  const [previzSceneData, setPrevizSceneData] = useState<{ cast: CastRow[]; props: PropRow[] } | null>(null)
  useEffect(() => {
    if ((!previzRecordOpen && !previzCreateOpen) || previzSceneData) return
    let cancelled = false
    Promise.all([fetchProjectCast(projectId), fetchProjectProps(projectId)])
      .then(([cast, propsList]) => {
        if (cancelled) return
        setPrevizSceneData({ cast: cast as CastRow[], props: propsList as PropRow[] })
      })
      .catch(() => {
        if (cancelled) return
        setPrevizSceneData({ cast: [], props: [] })
      })
    return () => {
      cancelled = true
    }
  }, [previzRecordOpen, previzCreateOpen, previzSceneData, projectId])

  // ── Скриншоты (frame captures) ──
  const [captures, setCaptures] = useState<ShotFrameCaptureRow[]>([])
  const [captureSubmitting, setCaptureSubmitting] = useState(false)
  const [timelineControls, setTimelineControls] = useState<TimelinePlaybackControls | null>(null)
  const [captureTargetMissing, setCaptureTargetMissing] = useState(false)
  // Компонент не пересоздаётся при смене шота — поздний ответ прежнего шота
  // не должен перетирать список нового (тот же приём, что fetchSeqRef выше).
  const captureSeqRef = useRef(0)

  const refetchCaptures = useCallback(() => {
    const seq = ++captureSeqRef.current
    listShotFrameCaptures(projectId, shotId)
      .then((rows) => {
        if (seq === captureSeqRef.current) setCaptures(rows)
      })
      .catch(() => {
        // Полоса скриншотов не критична: ошибку глотаем, список остаётся прежним.
      })
  }, [projectId, shotId])

  useEffect(() => {
    setCaptures([])
    refetchCaptures()
  }, [refetchCaptures])

  const activeFrameCaptureJob = useMemo(() => activeShotJob(jobs, shotId, FRAME_CAPTURE_JOB_KINDS), [jobs, shotId])
  useReloadOnJobFinish(activeFrameCaptureJob, refetchCaptures)

  // Цель захвата (gap/скрытая дорожка под плейхедом) меняется вне React-рендеров —
  // опрашиваем интервалом; setState с тем же значением рендер не вызывает.
  useEffect(() => {
    if (!timelineControls || previewTab !== "video") {
      setCaptureTargetMissing(false)
      return
    }
    const check = () => setCaptureTargetMissing(timelineControls.captureTarget() == null)
    check()
    const timer = window.setInterval(check, CAPTURE_TARGET_POLL_MS)
    return () => window.clearInterval(timer)
  }, [timelineControls, previewTab])

  const handleCaptureFrame = useCallback(async () => {
    if (!shot || captureSubmitting) return
    let target: { versionId: number | null; sourceMs: number } | null = null
    if (shot.timeline_editor) {
      target = timelineControls?.captureTarget() ?? null
    } else {
      const api = legacyPlaybackRef.current
      if (api) target = { versionId: null, sourceMs: Math.round(api.controls.currentMs()) }
    }
    if (!target) return
    setCaptureSubmitting(true)
    try {
      // POST отвечает мгновенно pending-строкой — она и есть оптимистичная плитка.
      const capture = await createShotFrameCapture(projectId, shot.id, {
        ms: target.sourceMs,
        versionId: target.versionId,
      })
      setCaptures((prev) => [...prev, capture])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("frameCaptures.captureFailed"))
    } finally {
      setCaptureSubmitting(false)
    }
  }, [shot, captureSubmitting, timelineControls, projectId, t])

  const handleDeleteCapture = useCallback(
    async (capture: ShotFrameCaptureRow) => {
      try {
        await deleteShotFrameCapture(projectId, shotId, capture.id)
        setCaptures((prev) => prev.filter((c) => c.id !== capture.id))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t("frameCaptures.deleteFailed"))
      }
    },
    [projectId, shotId, t],
  )

  const shotPipSources = useMemo(
    () => pipSourcesFromShot(shot?.previz, shot?.animatic_segment),
    [shot?.previz, shot?.animatic_segment],
  )

  if (loading && !shot) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <p className="text-sm text-ui-status-failed">{fetchError}</p>
        <Button type="button" size="sm" variant="secondary" onClick={fetchShot}>
          {t("common.retry")}
        </Button>
      </div>
    )
  }

  if (!shot) return null

  const previzTranscoding = previzStatus === "pending" || previzStatus === "processing"
  const showPrevizPrep = previzPrep != null || previzTranscoding
  const previzPrepStatus: ShotGenerationStatusView | null = showPrevizPrep
    ? {
        title:
          sceneNumber != null
            ? t("previz.preparingTitle", { scene: sceneNumber, number: shot.number })
            : t("previz.preparingTitleShort", { number: shot.number }),
        statusDetail: previzTranscoding ? "handling" : "saving",
        startedAt: previzPrep?.startedAt ?? shot.previz?.created_at ?? null,
      }
    : null

  const shotChars = shot.characters
  const shotLocation =
    shot.location_name || shot.location_variant_name
      ? { location: shot.location_name ?? "", variant: shot.location_variant_name ?? null }
      : null
  const linkedScene =
    shot.previz?.scene3d_id != null
      ? {
          name: shot.previz.scene3d_name?.trim() || shot.previz.name,
          imageUrl: shot.previz.scene3d_thumb_url ?? null,
        }
      : null

  const activeVideo = previewTab === "video"
  const activeStoryboard = previewTab === "storyboard"
  const activeShootingBoard = previewTab === "shooting_board"

  const frameLibrarySubtitle =
    frameLibraryTarget === "animatic"
      ? t("frameLibrary.subtitleAnimatic", { number: shot.number })
      : frameLibraryTarget === "previz"
        ? t("frameLibrary.subtitlePreviz", { number: shot.number })
        : frameLibraryTarget === "transition_left"
          ? t("transitionFrames.subtitleLeft", { number: shot.number })
          : frameLibraryTarget === "transition_right"
            ? t("transitionFrames.subtitleRight", { number: shot.number })
            : frameLibraryTarget === "shooting_board"
              ? t("frameLibrary.subtitleShootingBoard", { number: shot.number })
              : frameLibraryTarget === "video"
                ? t("frameLibrary.subtitleVideo", { number: shot.number })
                : t("frameLibrary.subtitleStoryboard", { number: shot.number })

  // Свежий ответ эндпоинта побеждает данные шота: он приходит раньше перезагрузки.
  const transitionState: TransitionFramesState = transitionFrames ?? {
    left_last_frame_url: shot.left_last_frame_url,
    right_first_frame_url: shot.right_first_frame_url,
    transition_left_frame_manual: shot.transition_left_frame_manual,
    transition_right_frame_manual: shot.transition_right_frame_manual,
  }

  const captureDisabled = shot.timeline_editor
    ? !timelineControls || captureTargetMissing || captureSubmitting
    : captureSubmitting

  const renderCaption = (label: string) => (
    <span className="max-w-[150px] truncate text-[10px] leading-tight text-ui-text-muted" title={label}>
      {label}
    </span>
  )

  const renderActionStack = (button: ReactNode, caption?: string) => (
    <div className="flex flex-col items-center gap-1">
      {button}
      {caption ? renderCaption(caption) : null}
    </div>
  )

  // Сплит-кнопка опорника: слева — «Кадр из шота N», справа — сегмент «из библиотеки».
  // Тот же chrome, что upload/download/copy рядом: IconButtonGroup + ghost.
  // Без предыдущего шота — одиночная «Из библиотеки».
  // Обе мутации скрыты при can_edit_content: false (левая кнопка существовала до флага — не трогаем).
  const renderPrevShotStoryboardButton = () => {
    if (isTransition) return null
    const prev = shot?.prev_shot_last_frame
    if (!prev) {
      if (!canEditContent) return null
      return renderActionStack(
        <IconButtonGroup className="self-start">
          <ActionHintTooltip label={t("preview.storyboardFromLibraryHint")}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-full gap-1.5 rounded-none border-0 px-2.5"
              disabled={storyboardUploading || !!busy}
              onClick={() => setFrameLibraryTarget("storyboard")}
            >
              {busy === "storyboardFromSource" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Images className="h-3.5 w-3.5" />
              )}
              {t("preview.storyboardFromLibrary")}
            </Button>
          </ActionHintTooltip>
        </IconButtonGroup>,
      )
    }
    const label = formatShotLabel(prev.scene_number, prev.shot_number)
    return renderActionStack(
      <IconButtonGroup className="self-start">
        <ActionHintTooltip label={t("preview.storyboardFromPrevShotHint", { label })}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-full gap-1.5 rounded-none border-0 px-2.5"
            disabled={storyboardUploading || !!busy}
            onClick={() => void handleStoryboardFromPrevShot()}
          >
            {busy === "storyboardFromPrev" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ImageDown className="h-3.5 w-3.5" />
            )}
            {t("preview.storyboardFromPrevShot", { label })}
          </Button>
        </ActionHintTooltip>
        {canEditContent ? (
          <ActionHintTooltip label={t("preview.pickFromLibrary")}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-full rounded-none border-0 px-2.5"
              disabled={storyboardUploading || !!busy}
              onClick={() => setFrameLibraryTarget("storyboard")}
              aria-label={t("preview.pickFromLibrary")}
            >
              {busy === "storyboardFromSource" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Images className="h-3.5 w-3.5" />
              )}
            </Button>
          </ActionHintTooltip>
        ) : null}
      </IconButtonGroup>,
    )
  }

  const renderImageOriginalActions = (
    url: string | null | undefined,
    filename: string,
    width?: number | null,
    height?: number | null,
  ) => (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={!url}
        onClick={() => handleDownloadOriginal(url, filename)}
      >
        <Download className="h-3.5 w-3.5" />
        {t("common.downloadOriginal")}
        {width && height ? (
          <span className="tabular-nums">
            {width}&times;{height}
          </span>
        ) : null}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={!url || copyingOriginal}
        onClick={() => void handleCopyOriginal(url)}
      >
        {copyingOriginal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
        {t("common.copy")}
      </Button>
    </>
  )

  const resolutionOptions = resolutionParamOptions(shotVideoModel)
  const inheritedResolution = inheritedShotVideoResolution(project, shotVideoModel)
  const resolutionOverride = optimisticOverride(shotVideoResolution, shot?.video_resolution)
  const hasResolutionOverride = Boolean(resolutionOverride?.toString().trim())
  const showDraftToggle = seedanceDraftEligible(effectiveModelKey, effectiveResolution)
  const draftChecked =
    shot?.video_draft == null ? Boolean(project?.byteplus_seedance_draft_default) : shot.video_draft === true
  const saveResolution = (override: string | null) => {
    if (!shot) return
    const nextEffective = effectiveShotVideoResolution(override, project, shotVideoModel)
    const clearDraft = !seedanceDraftEligible(effectiveModelKey, nextEffective)
    const shotIdToSave = shot.id
    setShot((prev) =>
      prev && prev.id === shotIdToSave
        ? { ...prev, video_resolution: override, video_draft: clearDraft ? null : prev.video_draft }
        : prev,
    )
    onVideoResolutionChange?.(override)
    void patchShotVideoResolution(projectId, shotIdToSave, override).catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("common.saveErrorRetry"))
    })
  }
  const videoGenerateLabel = (hasExistingVideo: boolean) => {
    if (showDraftToggle && draftChecked) return t("common.generateDraft")
    if (hasExistingVideo) return t("common.regenerate")
    if (shot?.video_generation_provider_transient) return t("canvas.modal.retryProviderTransient")
    return t("common.generateVideo")
  }
  const toggleDraft = () => {
    if (!shot || !canEditContent) return
    const next = !draftChecked
    const shotIdToSave = shot.id
    setShot((prev) => (prev && prev.id === shotIdToSave ? { ...prev, video_draft: next } : prev))
    void patchShotVideoDraft(projectId, shotIdToSave, next).catch((err: unknown) => {
      setShot((prev) => (prev && prev.id === shotIdToSave ? { ...prev, video_draft: shot.video_draft } : prev))
      toast.error(err instanceof Error ? err.message : t("common.saveErrorRetry"))
    })
  }
  const hasResolutionControl = resolutionOptions.length > 0
  const segmentFollowsDuration = hasResolutionControl || showDraftToggle
  const durationControl = (
    <VideoDurationPopover
      value={adaptiveActive ? ADAPTIVE_DURATION_SEC : currentDuration}
      options={withAdaptiveDurationOption(durationOptions, adaptiveAllowed)}
      placement={videoGenerateHost ? "above" : "below"}
      onChange={handleSelectDuration}
      triggerVariant="default"
      triggerClassName={cn("rounded-l-none border-l border-black/20", segmentFollowsDuration && "rounded-r-none")}
      multiShot={
        supportsMultiShot
          ? {
              checked: (shot?.video_shot_type ?? "single") === "multi",
              onChange: handleToggleMultiShot,
            }
          : undefined
      }
    />
  )
  const videoGenerateTrailing =
    durationOptions.length > 0 || hasResolutionControl || showDraftToggle ? (
      <>
        {durationOptions.length > 0 ? durationControl : null}
        {hasResolutionControl ? (
          <ShotVideoResolutionPopover
            value={effectiveResolution}
            inherited={inheritedResolution}
            hasOverride={hasResolutionOverride}
            options={resolutionOptions}
            disabled={!canEditContent}
            triggerClassName={cn(
              "rounded-l-none border-l border-black/20",
              showDraftToggle ? "rounded-r-none" : "rounded-r-lg",
              durationOptions.length > 0 && "rounded-l-none",
            )}
            onChange={saveResolution}
          />
        ) : null}
        {showDraftToggle ? (
          <ActionHintTooltip label={t(draftChecked ? "panel.draftOnHint" : "panel.draftOffHint")}>
            <Button
              type="button"
              size="sm"
              variant={draftChecked ? "default" : "ghost"}
              disabled={!canEditContent}
              aria-pressed={draftChecked}
              aria-label={t(draftChecked ? "panel.draftOnHint" : "panel.draftOffHint")}
              className={cn(
                "rounded-l-none rounded-r-lg border-l border-black/20 px-2",
                !draftChecked && "text-ui-text-muted",
              )}
              onClick={toggleDraft}
            >
              <FilePen className="h-3.5 w-3.5" />
            </Button>
          </ActionHintTooltip>
        ) : null}
      </>
    ) : undefined

  const videoGenerateButton = shot ? (
    <GenerateWithModelLabel
      label={videoGenerateLabel(hasVideo)}
      busyLabel={videoGenerateLabel(hasVideo)}
      modelLabel={videoGenLabel}
      cost={videoPrice}
      busy={busy === "generateVideo"}
      disabled={!!busy}
      onBeforeArm={!hasVideo && shot?.video_generation_provider_transient ? undefined : beforeArmGenerateVideo}
      onClick={startGenerateVideo}
      confirmDelayKey={confirmDelayKey("shot", shotId, "video")}
      collapseToIcon
      trailing={
        hasVideo && shot?.active_video_draft_finalize ? (
          <>
            <SeedanceDraftFinalizeButton
              appearance="segment"
              segmentEnd={!videoGenerateTrailing}
              finalize={shot.active_video_draft_finalize}
              projectId={projectId}
              onConfirm={async (resolution) => {
                if (!shot.active_video_version_id) return
                await postShotDraftFinalize(projectId, shot.id, shot.active_video_version_id, resolution)
                onDataChanged()
              }}
            />
            {videoGenerateTrailing}
          </>
        ) : (
          videoGenerateTrailing
        )
      }
    />
  ) : null
  const videoGenerateNode = videoGenerateButton ? (
    <ShotGenerationActions activeJob={activeVideoJob}>{videoGenerateButton}</ShotGenerationActions>
  ) : null
  const storyboardGenerateButton = (
    <GenerateWithModelLabel
      label={hasStoryboard ? t("common.regenerate") : t("common.generateFrame")}
      busyLabel={hasStoryboard ? t("common.regenerate") : t("common.generateFrame")}
      modelLabel={storyboardGenLabel}
      busy={busy === "generateStoryboard"}
      disabled={!!busy}
      onClick={handleGenerateStoryboard}
      confirmDelayKey={confirmDelayKey("shot", shotId, "storyboard")}
      collapseToIcon
    />
  )
  const storyboardGenerateNode = (
    <ShotGenerationActions activeJob={storyboardActionsJob} title={storyboardActionsTitle}>
      {storyboardGenerateButton}
    </ShotGenerationActions>
  )
  const shootingBoardGenerateButton = (
    <GenerateWithModelLabel
      label={hasShootingBoard ? t("common.regenerate") : t("common.generateShootingBoard")}
      busyLabel={hasShootingBoard ? t("common.regenerate") : t("common.generateShootingBoard")}
      modelLabel={storyboardGenLabel}
      busy={busy === "generateShootingBoard"}
      disabled={shootingBoardUploading || !!busy}
      onClick={handleGenerateShootingBoard}
      confirmDelayKey={confirmDelayKey("shot", shotId, "shootingBoard")}
      collapseToIcon
    />
  )

  const tabIconClass = "h-3.5 w-3.5"
  const previewTabs: FloatingTabPillTab[] = [
    {
      id: "storyboard",
      label: isTransition ? t("tabs.transition") : t("tabs.storyboard"),
      active: previewTab === "storyboard",
      filled: hasStoryboard,
      dirty: unreadTabs.has("storyboard"),
      icon: isTransition ? (
        <ArrowRightLeft className={tabIconClass} strokeWidth={2} />
      ) : (
        <ImageIcon className={tabIconClass} strokeWidth={2} />
      ),
      onClick: () => setPreviewTab("storyboard"),
    },
    {
      id: "video",
      label: t("tabs.video"),
      active: previewTab === "video",
      filled: hasVideo,
      dirty: unreadTabs.has("video"),
      icon: <VideoIcon className={tabIconClass} strokeWidth={2} />,
      onClick: () => setPreviewTab("video"),
    },
    ...(showShootingBoardTab
      ? [
          {
            id: "shooting_board",
            label: t("tabs.shootingBoard"),
            active: previewTab === "shooting_board",
            filled: hasShootingBoard,
            dirty: unreadTabs.has("shooting_board"),
            labelCollapsible: true,
            icon: <LayoutGrid className={tabIconClass} strokeWidth={2} />,
            onClick: () => setPreviewTab("shooting_board"),
          } satisfies FloatingTabPillTab,
        ]
      : []),
    {
      id: "animatic",
      label: t("tabs.animatic"),
      active: previewTab === "animatic",
      filled: hasAnimaticSegment,
      labelCollapsible: true,
      icon: <Film className={tabIconClass} strokeWidth={2} />,
      onClick: () => setPreviewTab("animatic"),
    },
    {
      id: "previz",
      label: t("tabs.previz"),
      active: previewTab === "previz",
      filled: Boolean(shot?.previz),
      labelCollapsible: true,
      icon: <Clapperboard className={tabIconClass} strokeWidth={2} />,
      onClick: () => setPreviewTab("previz"),
    },
  ]

  if (!project) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
      </div>
    )
  }

  return (
    <>
      <div className="pb-3">
        <div className="pointer-events-none sticky top-2 z-40 h-0">
          <div className="flex w-full max-w-full justify-center px-2">
            <FloatingTabPill
              className="pointer-events-auto"
              tabs={previewTabs}
              dirtyTone="approved"
              dirtyAriaLabel={t("tabs.unseenAfterGeneration")}
            />
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 overflow-visible">
          {/* ── Video tab ── */}
          {/* Плеер всегда в DOM при наличии видео (скрыт CSS на других вкладках) — буфер не сбрасывается. */}
          {hasVideo && shot.timeline_editor ? (
            <div className={cn("w-full", activeVideo ? "" : "hidden")}>
              <TimelineEditor
                key={`${shot.id}-${shot.active_video_version_id ?? "none"}`}
                projectId={projectId}
                shotId={shot.id}
                data={shot.timeline_editor}
                active={activeVideo}
                renderJobActive={activeVideoJob?.job_kind === "shot_timeline_render"}
                lipsyncJobs={activeLipsyncJobs}
                correctionJobs={activeCorrectionJobs}
                onRenderStarted={onDataChanged}
                onPlaybackReady={setTimelineControls}
                videoCorrectionEnabled={videoCorrectionEnabled && !isTransition}
                videoCorrectionAdvanced={videoCorrectionAdvanced}
                videoCorrectionTrimMaxSeconds={videoCorrectionTrimMaxSeconds}
                videoCorrectionMarkupMax={videoCorrectionMarkupMax}
                onCorrectionQueued={onDataChanged}
                pipSources={shotPipSources}
                videoCorner={shot.active_video_draft_finalize ? <SeedanceDraftBadge /> : null}
                captureAction={
                  canEditContent
                    ? {
                        onCapture: () => void handleCaptureFrame(),
                        busy: captureSubmitting,
                        disabled: captureDisabled,
                        label: t("frameCaptures.captureHint"),
                      }
                    : undefined
                }
              />
            </div>
          ) : null}
          {hasVideo && !shot.timeline_editor && activeVideo ? (
            <SingleClipPlayer
              key={shot.video_clip_url!}
              sourceId={shot.id}
              streamUrl={shot.video_clip_url!}
              active={activeVideo}
              autoPlay={false}
              defaultLoop
              showTrackHeaders={false}
              pipSources={shotPipSources}
              onPlaybackReady={(api) => {
                legacyPlaybackRef.current = api
              }}
              shellOverlay={
                shot.active_video_draft_finalize ? (
                  <span className="absolute bottom-2 left-2">
                    <SeedanceDraftBadge />
                  </span>
                ) : undefined
              }
              className="w-full"
            />
          ) : null}
          {activeVideo && hasVideo ? (
            <>
              {shot.video_generation_note ? (
                <p className="max-w-sm text-center text-xs leading-relaxed text-ui-text-secondary">
                  {shot.video_generation_note}
                </p>
              ) : null}
              <div className="flex flex-wrap items-start justify-center gap-2">
                {videoGenerateHost ? null : videoGenerateNode}
                <input
                  ref={videoFileRef}
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void handleVideoFile(f)
                  }}
                />
                <IconButtonGroup className="self-start">
                  <ActionHintTooltip label={t("common.upload")}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-full rounded-none border-0 px-2.5"
                      disabled={videoUploading || !!busy}
                      onClick={() => videoFileRef.current?.click()}
                      aria-label={t("common.upload")}
                    >
                      {videoUploading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Upload className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </ActionHintTooltip>
                  {canEditContent ? (
                    <ActionHintTooltip label={t("preview.pickFromLibrary")}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full rounded-none border-0 px-2.5"
                        disabled={videoUploading || !!busy}
                        onClick={() => setFrameLibraryTarget("video")}
                        aria-label={t("preview.pickFromLibrary")}
                      >
                        {busy === "videoFromSource" ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Images className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </ActionHintTooltip>
                  ) : null}
                  <ActionHintTooltip label={t("common.downloadOriginal")}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-full gap-1.5 rounded-none border-0 px-2.5"
                      disabled={!shot.video_download_url && !shot.video_clip_url}
                      onClick={() =>
                        handleDownloadOriginal(
                          shot.video_download_url ?? shot.video_clip_url,
                          `video_shot${shot.number}.mp4`,
                        )
                      }
                      aria-label={t("common.downloadOriginal")}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {shot.video_file_size != null ? (
                        <span className="text-[10px] tabular-nums">{formatBytes(shot.video_file_size)}</span>
                      ) : null}
                    </Button>
                  </ActionHintTooltip>
                  <ActionHintTooltip label={t("postProduction.action")}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-full rounded-none border-0 px-2.5"
                      disabled={!shot.active_video_version_id || !!busy}
                      onClick={() => setPostProductionOpen(true)}
                      aria-label={t("postProduction.action")}
                    >
                      <PostProductionIcon className="h-3.5 w-3.5" />
                    </Button>
                  </ActionHintTooltip>
                  {/* У таймлайн-шотов кнопка живёт в тулбаре плеера; у legacy-плеера тулбара нет. */}
                  {canEditContent && !shot.timeline_editor ? (
                    <ActionHintTooltip label={t("frameCaptures.captureHint")}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-full rounded-none border-0 px-2.5"
                        disabled={captureDisabled}
                        onClick={() => void handleCaptureFrame()}
                        aria-label={t("frameCaptures.captureHint")}
                      >
                        {captureSubmitting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Camera className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </ActionHintTooltip>
                  ) : null}
                </IconButtonGroup>
              </div>
              {videoUploadError ? (
                <p className="text-center text-xs leading-relaxed text-ui-status-failed/95">{videoUploadError}</p>
              ) : null}
            </>
          ) : null}
          {activeVideo && !hasVideo ? (
            <div className={emptyMediaFrameClass}>
              <p>{isTransition ? t("preview.videoTransitionNotGenerated") : t("preview.videoNotGenerated")}</p>
              {shot.video_generation_error ? (
                <p
                  className="line-clamp-3 max-w-sm text-xs leading-relaxed break-all text-ui-status-rendering/95"
                  title={shot.video_generation_error}
                >
                  {shot.video_generation_error}
                </p>
              ) : null}
              <div className="flex flex-wrap items-start justify-center gap-2 pt-1">
                <ShotGenerationActions activeJob={videoGenerateHost ? null : activeVideoJob}>
                  {videoGenerateHost ? null : videoGenerateButton}
                  <input
                    ref={videoFileRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void handleVideoFile(f)
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={videoUploading || !!busy}
                    onClick={() => videoFileRef.current?.click()}
                  >
                    {videoUploading ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {t("common.upload")}
                  </Button>
                  {canEditContent ? (
                    <ActionHintTooltip label={t("preview.videoFromLibraryHint")}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="self-start"
                        disabled={videoUploading || !!busy}
                        onClick={() => setFrameLibraryTarget("video")}
                      >
                        {busy === "videoFromSource" ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Images className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {t("preview.videoFromLibrary")}
                      </Button>
                    </ActionHintTooltip>
                  ) : null}
                </ShotGenerationActions>
              </div>
              {videoUploadError ? (
                <p className="text-center text-xs leading-relaxed text-ui-status-failed/95">{videoUploadError}</p>
              ) : null}
            </div>
          ) : null}

          {/* ── Shooting board tab ── */}
          {activeShootingBoard && showShootingBoardTab && hasShootingBoard ? (
            <>
              <div
                className={cn(
                  "relative max-h-[min(70vh,600px)] w-full overflow-hidden rounded-lg bg-black shadow-inner",
                  shootingBoardFrameClass,
                )}
              >
                <button
                  type="button"
                  className="h-full w-full cursor-zoom-in"
                  onClick={() => setPreviewLightbox("shooting_board")}
                  aria-label={t("tabs.shootingBoard")}
                >
                  <img src={shootingBoardPreviewUrlEffective} alt="" className="h-full w-full object-contain" />
                </button>
              </div>
              <div className="flex flex-wrap items-start justify-center gap-2">
                <ShotGenerationActions activeJob={shootingBoardActionsJob} title={shootingBoardActionsTitle}>
                  <input
                    ref={shootingBoardFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void handleShootingBoardFile(f)
                    }}
                  />
                  {shootingBoardGenerateHost ? null : shootingBoardGenerateButton}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={shootingBoardUploading || !!busy}
                    onClick={() => shootingBoardFileRef.current?.click()}
                  >
                    {shootingBoardUploading ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {t("common.upload")}
                  </Button>
                  {canEditContent ? (
                    <ActionHintTooltip label={t("preview.shootingBoardFromLibraryHint")}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="self-start"
                        disabled={shootingBoardUploading || !!busy}
                        onClick={() => setFrameLibraryTarget("shooting_board")}
                      >
                        {busy === "shootingBoardFromSource" ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Images className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {t("preview.shootingBoardFromLibrary")}
                      </Button>
                    </ActionHintTooltip>
                  ) : null}
                </ShotGenerationActions>
                {renderImageOriginalActions(
                  shootingBoardBrowserUrl,
                  `shooting_board_shot${shot.number}.png`,
                  shot.shooting_board_full_width,
                  shot.shooting_board_full_height,
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(showAnimaticStripPanel && "bg-ui-surface-raised text-ui-accent-text")}
                  disabled={shootingBoardUploading || (!!busy && busy !== "buildAnimaticStrip")}
                  onClick={() => setShowAnimaticStripPanel((v) => !v)}
                >
                  <Film className="mr-1.5 h-3.5 w-3.5" />
                  {t("common.sliceFromAnimatic")}
                </Button>
              </div>
              {showAnimaticStripPanel ? (
                <div className="w-full max-w-md space-y-2 rounded-lg border border-ui-border px-3 py-2.5 dark:bg-black/25 light:bg-clay-1">
                  <p className="text-[10px] leading-snug text-ui-text-muted">{t("common.animaticStripHint")}</p>
                  <label className="block text-[10px] text-ui-text-muted">
                    {t("common.panelCount")}
                    <select
                      className="mt-0.5 w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-2 py-1.5 text-[11px] text-ui-text-primary"
                      value={animaticStripPanelCount}
                      onChange={(e) => setAnimaticStripPanelCount(Number(e.target.value))}
                    >
                      {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <ShotGenerationActions activeJob={shootingBoardActionsJob} title={shootingBoardActionsTitle}>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full justify-start text-[11px]"
                      disabled={!hasAnimaticSegment || !!busy}
                      onClick={handleBuildShootingBoardFromAnimatic}
                    >
                      {busy === "buildAnimaticStrip" ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Film className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {t("common.updateFromAnimatic")}
                    </Button>
                  </ShotGenerationActions>
                  {!hasAnimaticSegment ? (
                    <p className="text-[10px] text-ui-text-secondary">{t("common.noAnimaticSegment")}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
          {activeShootingBoard && showShootingBoardTab && !hasShootingBoard ? (
            <div className={shootingBoardEmptyClass}>
              <p>{t("preview.shootingBoardNotCreated")}</p>
              {shot.shooting_board_generation_error?.trim() ? (
                <p
                  className="line-clamp-3 max-w-sm text-xs leading-relaxed break-all text-ui-status-rendering/95"
                  title={shot.shooting_board_generation_error}
                >
                  {shot.shooting_board_generation_error}
                </p>
              ) : null}
              {shootingBoardUploadError ? (
                <p className="max-w-sm text-xs leading-relaxed text-ui-status-failed/95">{shootingBoardUploadError}</p>
              ) : null}
              <div className="flex flex-wrap items-start justify-center gap-2 pt-1">
                <ShotGenerationActions activeJob={shootingBoardActionsJob} title={shootingBoardActionsTitle}>
                  <input
                    ref={shootingBoardFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void handleShootingBoardFile(f)
                    }}
                  />
                  {shootingBoardGenerateHost ? null : shootingBoardGenerateButton}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="self-start"
                    disabled={shootingBoardUploading || !!busy}
                    onClick={() => shootingBoardFileRef.current?.click()}
                  >
                    {shootingBoardUploading ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {t("common.upload")}
                  </Button>
                  {canEditContent ? (
                    <ActionHintTooltip label={t("preview.shootingBoardFromLibraryHint")}>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="self-start"
                        disabled={shootingBoardUploading || !!busy}
                        onClick={() => setFrameLibraryTarget("shooting_board")}
                      >
                        {busy === "shootingBoardFromSource" ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Images className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {t("preview.shootingBoardFromLibrary")}
                      </Button>
                    </ActionHintTooltip>
                  ) : null}
                </ShotGenerationActions>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(showAnimaticStripPanel && "bg-ui-surface-raised text-ui-accent-text")}
                  disabled={shootingBoardUploading || (!!busy && busy !== "buildAnimaticStrip")}
                  onClick={() => setShowAnimaticStripPanel((v) => !v)}
                >
                  <Film className="mr-1.5 h-3.5 w-3.5" />
                  {t("common.sliceFromAnimatic")}
                </Button>
              </div>
              {showAnimaticStripPanel ? (
                <div className="w-full max-w-md space-y-2 rounded-lg border border-ui-border px-3 py-2.5 dark:bg-black/25 light:bg-clay-1">
                  <p className="text-[10px] leading-snug text-ui-text-muted">{t("common.animaticFramesHint")}</p>
                  <label className="block text-[10px] text-ui-text-muted">
                    {t("common.panelCount")}
                    <select
                      className="mt-0.5 w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-2 py-1.5 text-[11px] text-ui-text-primary"
                      value={animaticStripPanelCount}
                      onChange={(e) => setAnimaticStripPanelCount(Number(e.target.value))}
                    >
                      {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <ShotGenerationActions activeJob={shootingBoardActionsJob} title={shootingBoardActionsTitle}>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="w-full justify-start text-[11px]"
                      disabled={shootingBoardUploading || !hasAnimaticSegment || !!busy}
                      onClick={handleBuildShootingBoardFromAnimatic}
                    >
                      {busy === "buildAnimaticStrip" ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Film className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {t("common.buildStripToShootingBoard")}
                    </Button>
                  </ShotGenerationActions>
                  {!hasAnimaticSegment ? (
                    <p className="text-[10px] text-ui-text-secondary">{t("common.noAnimaticSegment")}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ── Storyboard tab ── */}
          {/* Коллаж показываем всегда, даже когда автоматика не нашла кадров: именно тогда
              ручное назначение и нужно, а прежний пустой экран не давал куда нажать. */}
          {activeStoryboard && isTransition ? (
            <>
              {/* Сцена во всю ширину, коллаж внутри — своего формата кадра и по центру.
                  Задавать формат самой сцене нельзя: при w-full + aspect ширина остаётся
                  полной, max-h её не сжимает, и на вертикальном кадре object-cover в
                  половинах раздувал картинку до нечитаемого зума. */}
              <div className="relative flex h-[min(70vh,600px)] w-full items-center justify-center overflow-hidden rounded-lg bg-black shadow-inner">
                <div className={cn("relative flex h-full items-stretch", ff.aspect)}>
                  <TransitionFrameHalf
                    url={transitionState.left_last_frame_url}
                    side="left"
                    manual={transitionState.transition_left_frame_manual}
                    noFrameLabel={canEditContent ? "" : t("common.noFrame")}
                  />
                  <TransitionFrameHalf
                    url={transitionState.right_first_frame_url}
                    side="right"
                    manual={transitionState.transition_right_frame_manual}
                    noFrameLabel={canEditContent ? "" : t("common.noFrame")}
                  />
                  {/* Декор поверх половин: без pointer-events-none он забирал бы себе
                    наведение и клики по обеим половинам — кнопки выбора не появлялись. */}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-amber-600/90 p-2 shadow-lg shadow-black/40">
                      <ArrowRightLeft className="h-5 w-5 text-ui-text-primary" />
                    </div>
                  </div>
                </div>
                {/* Разделитель и зоны управления — по сцене, а не по узкой полоске кадра:
                    пополам делится именно вся область, и кнопки центрируются в её половинах. */}
                <div className="pointer-events-none absolute inset-y-0 left-1/2 z-10 w-px bg-amber-400/60" />
                {canEditContent ? (
                  <>
                    <TransitionFrameControls
                      side="left"
                      hasFrame={Boolean(transitionState.left_last_frame_url)}
                      manual={transitionState.transition_left_frame_manual}
                      busy={transitionBusySide === "left"}
                      onPick={() => setFrameLibraryTarget("transition_left")}
                      onReset={() => void handleResetTransitionFrame("left")}
                    />
                    <TransitionFrameControls
                      side="right"
                      hasFrame={Boolean(transitionState.right_first_frame_url)}
                      manual={transitionState.transition_right_frame_manual}
                      busy={transitionBusySide === "right"}
                      onPick={() => setFrameLibraryTarget("transition_right")}
                      onReset={() => void handleResetTransitionFrame("right")}
                    />
                  </>
                ) : null}
              </div>
              {/* Подсказка под сценой, а не поверх неё: внизу половин живут кнопки. */}
              {!transitionState.left_last_frame_url || !transitionState.right_first_frame_url ? (
                <p className="text-center text-xs leading-relaxed text-ui-text-secondary">
                  {t("preview.transitionFramesNotReady")}
                </p>
              ) : null}
            </>
          ) : activeStoryboard && hasStoryboard ? (
            <>
              <div
                className={cn(
                  "relative max-h-[min(70vh,600px)] w-full overflow-hidden rounded-lg bg-black shadow-inner",
                  ff.aspect,
                )}
              >
                <button
                  type="button"
                  className="h-full w-full cursor-zoom-in"
                  onClick={() => setPreviewLightbox("storyboard")}
                  aria-label={t("tabs.storyboard")}
                >
                  <img src={shot.storyboard_preview_url!} alt="" className="h-full w-full object-contain" />
                </button>
              </div>
              <div className="flex flex-wrap items-start justify-center gap-2">
                {storyboardGenerateHost ? null : storyboardGenerateNode}
                {!isTransition ? (
                  <>
                    <input
                      ref={storyboardFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) void handleStoryboardFile(f)
                      }}
                    />
                    <IconButtonGroup className="self-start">
                      <ActionHintTooltip label={t("common.upload")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full rounded-none border-0 px-2.5"
                          disabled={storyboardUploading || !!busy}
                          onClick={() => storyboardFileRef.current?.click()}
                          aria-label={t("common.upload")}
                        >
                          {storyboardUploading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Upload className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </ActionHintTooltip>
                      <ActionHintTooltip label={t("common.downloadOriginal")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full gap-1.5 rounded-none border-0 px-2.5"
                          disabled={!storyboardBrowserUrl}
                          onClick={() =>
                            handleDownloadOriginal(storyboardBrowserUrl, `storyboard_shot${shot.number}.png`)
                          }
                          aria-label={t("common.downloadOriginal")}
                        >
                          <Download className="h-3.5 w-3.5" />
                          {shot.storyboard_full_width && shot.storyboard_full_height ? (
                            <span className="text-[10px] tabular-nums">
                              {shot.storyboard_full_width}&times;{shot.storyboard_full_height}
                            </span>
                          ) : null}
                        </Button>
                      </ActionHintTooltip>
                      <ActionHintTooltip label={t("common.copy")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full rounded-none border-0 px-2.5"
                          disabled={!storyboardBrowserUrl || copyingOriginal}
                          onClick={() => void handleCopyOriginal(storyboardBrowserUrl)}
                          aria-label={t("common.copy")}
                        >
                          {copyingOriginal ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </ActionHintTooltip>
                    </IconButtonGroup>
                    {renderPrevShotStoryboardButton()}
                  </>
                ) : null}
              </div>
              {storyboardUploadError ? (
                <p className="text-center text-xs leading-relaxed text-ui-status-failed/95">{storyboardUploadError}</p>
              ) : null}
            </>
          ) : activeStoryboard ? (
            // Переход сюда больше не попадает: его коллаж рендерится всегда, даже пустым.
            <div className={emptyMediaFrameClass}>
              <p>{t("preview.storyboardNotGenerated")}</p>
              {shot.storyboard_generation_error?.trim() ? (
                <p
                  className="line-clamp-3 max-w-sm text-xs leading-relaxed break-all text-ui-status-rendering/95"
                  title={shot.storyboard_generation_error}
                >
                  {shot.storyboard_generation_error}
                </p>
              ) : null}
              {storyboardUploadError ? (
                <p className="max-w-sm text-xs leading-relaxed text-ui-status-failed/95">{storyboardUploadError}</p>
              ) : null}
              <div className="flex flex-wrap items-start justify-center gap-2 pt-1">
                <ShotGenerationActions
                  activeJob={storyboardGenerateHost ? null : storyboardActionsJob}
                  title={storyboardActionsTitle}
                >
                  {storyboardGenerateHost ? null : storyboardGenerateButton}
                  <input
                    ref={storyboardFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void handleStoryboardFile(f)
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="self-start"
                    disabled={storyboardUploading || !!busy}
                    onClick={() => storyboardFileRef.current?.click()}
                  >
                    {storyboardUploading ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {t("common.upload")}
                  </Button>
                  {renderPrevShotStoryboardButton()}
                </ShotGenerationActions>
              </div>
            </div>
          ) : null}

          {/* ── Animatic tab ── */}
          {/* Общий на вкладку выбор файла: и замена при готовом аниматике,
              и первая загрузка в пустом состоянии. */}
          <input
            ref={animaticFileRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleUploadAnimatic(file)
              e.target.value = ""
            }}
          />
          {previewTab === "animatic" && shot.animatic_segment ? (
            <div className="flex w-full flex-col gap-4">
              {(() => {
                const seg = shot.animatic_segment
                const referenceStreamUrl =
                  seg.reference_clip_with_audio_stream_url ??
                  seg.reference_clip_stream_url ??
                  seg.reference_clip_with_audio_url ??
                  seg.reference_clip_url
                const animaticStreamUrl = seg.animatic_video_stream_url ?? seg.animatic_video_url
                const hasPlayer = Boolean(referenceStreamUrl || animaticStreamUrl)
                return (
                  <>
                    {hasPlayer ? (
                      <ShotAnimaticSegmentPlayer
                        segmentId={seg.id}
                        startMs={seg.start_ms}
                        endMs={seg.end_ms}
                        referenceClipStreamUrl={referenceStreamUrl}
                        animaticVideoStreamUrl={animaticStreamUrl}
                        timelineFrameUrls={seg.timeline_frame_urls}
                        timelineFrameAspectRatio={seg.timeline_frame_aspect_ratio}
                        sourceDurationMs={seg.animatic_source_duration_ms}
                        frameRate={project.effective_video_output_fps ?? DEFAULT_VIDEO_OUTPUT_FPS}
                        className="w-full max-w-full"
                      />
                    ) : (
                      <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-ui-border-strong bg-ui-surface-sunken text-sm text-ui-text-muted">
                        {t("common.referenceClipNotCreated")}
                      </div>
                    )}
                    <div className="w-full space-y-3 rounded-lg border border-ui-border p-4 dark:bg-black/25 light:bg-clay-1">
                      <div className="flex flex-wrap items-center gap-4 text-xs text-ui-text-secondary">
                        <span>{t("common.segmentPosition", { position: seg.position })}</span>
                        <span>
                          {formatMs(seg.start_ms)} — {formatMs(seg.end_ms)}
                        </span>
                        {seg.episode_number != null && (
                          <span>
                            {seg.season_number != null ? `S${seg.season_number}` : ""}E{seg.episode_number}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {seg.poster_url && (
                          <button
                            type="button"
                            onClick={() => setZoomedFrame(seg.poster_url)}
                            className="group relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken"
                          >
                            <img src={seg.poster_url} alt={t("common.poster")} className="h-full w-full object-cover" />
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] text-ui-text-primary opacity-0 transition-opacity group-hover:opacity-100 dark:bg-black/50 light:bg-clay-1/80">
                              {t("common.poster")}
                            </span>
                          </button>
                        )}
                        {seg.first_frame_url && (
                          <button
                            type="button"
                            onClick={() => setZoomedFrame(seg.first_frame_url)}
                            className="group relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken"
                          >
                            <img
                              src={seg.first_frame_url}
                              alt={t("common.first")}
                              className="h-full w-full object-cover"
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] text-ui-text-primary opacity-0 transition-opacity group-hover:opacity-100 dark:bg-black/50 light:bg-clay-1/80">
                              {t("common.first")}
                            </span>
                          </button>
                        )}
                        {seg.last_frame_url && (
                          <button
                            type="button"
                            onClick={() => setZoomedFrame(seg.last_frame_url)}
                            className="group relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken"
                          >
                            <img
                              src={seg.last_frame_url}
                              alt={t("common.last")}
                              className="h-full w-full object-cover"
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] text-ui-text-primary opacity-0 transition-opacity group-hover:opacity-100 dark:bg-black/50 light:bg-clay-1/80">
                              {t("common.last")}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-ui-text-secondary"
                        disabled={animaticLinking || animaticUploadPercent !== null}
                        onClick={() => animaticFileRef.current?.click()}
                      >
                        {animaticUploadPercent !== null ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <UploadCloud className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {animaticUploadPercent !== null
                          ? t("common.uploadingAnimatic", { percent: animaticUploadPercent })
                          : t("common.replaceAnimatic")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-ui-text-secondary"
                        disabled={animaticLinking || animaticUploadPercent !== null}
                        onClick={() => setFrameLibraryTarget("animatic")}
                      >
                        <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
                        {t("common.pickAnimaticFromLibrary")}
                      </Button>
                      {/* Открепление, а не удаление, — для обоих видов сегмента:
                          сегмент ленты остаётся свободным, а свой ролик — историей
                          версий в библиотеке шота; файлы не удаляются. */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-ui-text-secondary hover:text-ui-status-failed"
                        disabled={animaticLinking || animaticUploadPercent !== null}
                        onClick={handleUnlinkSegment}
                      >
                        {animaticLinking ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Link2Off className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {t("common.unlinkSegment")}
                      </Button>
                    </div>
                    {animaticUploadError && (
                      <p className="text-center text-[11px] text-ui-status-failed">{animaticUploadError}</p>
                    )}
                  </>
                )
              })()}
            </div>
          ) : previewTab === "animatic" && !shot.animatic_segment ? (
            <div className="flex w-full flex-col gap-4">
              {/* Свой ролик загружают сюда или берут любое видео из библиотеки —
                  там же сегменты ленты и ранее прикреплённые аниматики шота. */}
              <div
                className={emptyMediaFrameClass}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const file = e.dataTransfer.files[0]
                  if (file) void handleUploadAnimatic(file)
                }}
              >
                <Film className="h-8 w-8 text-ui-text-secondary" />
                {awaitingAnimatic ? (
                  <p className="flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t("common.preparingAnimatic")}
                  </p>
                ) : (
                  <>
                    <p>{t("common.uploadAnimaticHint")}</p>
                    <p className="text-xs text-ui-text-secondary">
                      {t("common.uploadAnimaticHintDetailBefore")}
                      <Link
                        href={animaticsHref}
                        className="font-medium text-ui-link underline-offset-2 hover:text-ui-accent-text hover:underline"
                        onClick={() => useShotPanelStore.getState().close()}
                      >
                        {t("common.uploadAnimaticHintDetailLink")}
                      </Link>
                      {t("common.uploadAnimaticHintDetailAfter")}
                    </p>
                  </>
                )}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={animaticUploadPercent !== null}
                    onClick={() => animaticFileRef.current?.click()}
                  >
                    {animaticUploadPercent !== null ? (
                      <>
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        {t("common.uploadingAnimatic", { percent: animaticUploadPercent })}
                      </>
                    ) : (
                      <>
                        <UploadCloud className="mr-1.5 h-3.5 w-3.5" />
                        {t("common.uploadAnimaticButton")}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    disabled={animaticLinking || animaticUploadPercent !== null}
                    onClick={() => setFrameLibraryTarget("animatic")}
                  >
                    <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
                    {t("common.pickAnimaticFromLibrary")}
                  </Button>
                </div>
                {animaticUploadError && <p className="text-[11px] text-ui-status-failed">{animaticUploadError}</p>}
              </div>
            </div>
          ) : previewTab === "previz" ? (
            <div className="flex w-full flex-col gap-4">
              {/* Общий выбор файла на замену и первую загрузку — как у аниматика. */}
              <input
                ref={previzFileRef}
                type="file"
                accept="video/webm,video/mp4"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void handleUploadPreviz(file)
                  e.target.value = ""
                }}
              />
              {shot.previz ? (
                (() => {
                  const previz = shot.previz
                  const previzAspect =
                    previz.timeline_frame_aspect_ratio != null && previz.timeline_frame_aspect_ratio > 0
                      ? previz.timeline_frame_aspect_ratio
                      : frameFormatRatio(
                          effectiveFrameFormat(
                            optimisticOverride(shotFrameFormat, shot.frame_format),
                            project.frame_format ?? "9:16",
                          ),
                        )
                  const depthUrl =
                    previzDepthVariant === "vis" ? (previz.depth_color_url ?? null) : (previz.depth_url ?? null)
                  const depthReady = Boolean(previz.depth_current && depthUrl)
                  return (
                    <>
                      {previzMedia === "depth" ? (
                        depthInFlight ? (
                          <div className="relative aspect-video max-h-[min(70vh,600px)] w-full overflow-hidden rounded-lg bg-black shadow-inner">
                            <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-ui-text-muted">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>{t("previz.depthGenerating")}</span>
                            </div>
                          </div>
                        ) : depthReady && depthUrl ? (
                          <SingleClipPlayer
                            key={`${previz.id}-${previzDepthVariant}`}
                            sourceId={previz.id}
                            streamUrl={depthUrl}
                            durationMs={previz.duration_ms}
                            frameRate={project.effective_video_output_fps ?? DEFAULT_VIDEO_OUTPUT_FPS}
                            aspectRatio={previzAspect}
                            defaultLoop={false}
                            showTrackHeaders={false}
                            autoPlay={false}
                            className="w-full max-w-full"
                          />
                        ) : (
                          <div className={emptyMediaFrameClass}>
                            <p>{t("previz.depthNotGenerated")}</p>
                            {previz.depth_status === "failed" && previz.depth_error ? (
                              <p
                                className="line-clamp-3 max-w-sm text-xs leading-relaxed break-all text-ui-status-failed"
                                title={previz.depth_error}
                              >
                                {previz.depth_error}
                              </p>
                            ) : null}
                            {previz.status !== "ready" ? (
                              <p className="text-xs text-ui-text-secondary">{t("previz.depthNeedsMp4")}</p>
                            ) : null}
                            <div className="flex flex-wrap items-start justify-center gap-2 pt-1">
                              <Button
                                type="button"
                                variant="default"
                                size="sm"
                                className="self-start"
                                disabled={previz.status !== "ready" || depthGenerateBusy}
                                onClick={handleGeneratePrevizDepth}
                              >
                                {t("previz.depthGenerate")}
                              </Button>
                            </div>
                          </div>
                        )
                      ) : previz.url ? (
                        <SingleClipPlayer
                          sourceId={previz.id}
                          streamUrl={previz.url}
                          durationMs={previz.duration_ms}
                          frameRate={project.effective_video_output_fps ?? DEFAULT_VIDEO_OUTPUT_FPS}
                          aspectRatio={previzAspect}
                          timelineFrameUrls={previz.timeline_frame_urls}
                          defaultLoop={false}
                          showTrackHeaders={false}
                          autoPlay={false}
                          className="w-full max-w-full"
                        />
                      ) : previz.status === "pending" || previz.status === "processing" ? null : (
                        <div className="relative aspect-video max-h-[min(70vh,600px)] w-full overflow-hidden rounded-lg bg-black shadow-inner">
                          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-ui-text-muted">
                            <span>{t("previz.videoMissing")}</span>
                          </div>
                        </div>
                      )}
                      {previzMedia === "clip" && previz.status === "failed" ? (
                        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-ui-status-failed">
                          <span>{t("workspace:library.previzes.status.failed")}</span>
                        </div>
                      ) : null}
                      <div className="mb-4 flex flex-wrap items-start justify-center gap-2">
                        <SegmentedControl
                          items={[
                            { id: "clip", label: t("previz.viewClip") },
                            { id: "depth", label: t("previz.viewDepth") },
                          ]}
                          value={previzMedia}
                          onChange={(id) => setPrevizMedia(id === "depth" ? "depth" : "clip")}
                        />
                        {previzMedia === "depth" ? (
                          <SegmentedControl
                            items={[
                              { id: "gray", label: t("previz.depthGray") },
                              { id: "vis", label: t("previz.depthColor") },
                            ]}
                            value={previzDepthVariant}
                            onChange={(id) => handlePrevizDepthVariant(id === "vis" ? "vis" : "gray")}
                          />
                        ) : null}
                        <ShotGenerationActions activeJob={null} status={previzPrepStatus} expandable={false}>
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            disabled={previzBusy}
                            onClick={() => void handleRecordPreviz()}
                          >
                            <VideoIcon className="h-3.5 w-3.5" />
                            {t("previz.record")}
                          </Button>
                        </ShotGenerationActions>
                        <IconButtonGroup className="self-start">
                          <ActionHintTooltip label={t("previz.replace")}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-full rounded-none border-0 px-2.5"
                              disabled={previzBusy}
                              onClick={() => previzFileRef.current?.click()}
                              aria-label={t("previz.replace")}
                            >
                              {previzBusy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Upload className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </ActionHintTooltip>
                          <ActionHintTooltip label={t("previz.pickFromLibrary")}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-full rounded-none border-0 px-2.5"
                              disabled={previzBusy}
                              onClick={() => setFrameLibraryTarget("previz")}
                              aria-label={t("previz.pickFromLibrary")}
                            >
                              <Images className="h-3.5 w-3.5" />
                            </Button>
                          </ActionHintTooltip>
                          {/* Отвязка, не удаление: превиз остаётся в библиотеке превизов. */}
                          <ActionHintTooltip label={t("previz.unlink")}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-full rounded-none border-0 px-2.5"
                              disabled={previzBusy || showPrevizPrep}
                              onClick={() => void handleUnlinkPreviz()}
                              aria-label={t("previz.unlink")}
                            >
                              <Unlink className="h-3.5 w-3.5" />
                            </Button>
                          </ActionHintTooltip>
                          <ActionHintTooltip label={t("common.downloadOriginal")}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-full gap-1.5 rounded-none border-0 px-2.5"
                              disabled={!previz.download_url && !previz.url}
                              onClick={() =>
                                handleDownloadOriginal(
                                  previz.download_url ?? previz.url,
                                  `previz_shot${shot.number}.mp4`,
                                )
                              }
                              aria-label={t("common.downloadOriginal")}
                            >
                              <Download className="h-3.5 w-3.5" />
                              {previz.file_size != null ? (
                                <span className="text-[10px] tabular-nums">{formatBytes(previz.file_size)}</span>
                              ) : null}
                            </Button>
                          </ActionHintTooltip>
                        </IconButtonGroup>
                      </div>
                    </>
                  )
                })()
              ) : (
                <div
                  className={cn(emptyMediaFrameClass, showPrevizPrep && "relative")}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (showPrevizPrep) return
                    const file = e.dataTransfer.files[0]
                    if (file) void handleUploadPreviz(file)
                  }}
                >
                  <Clapperboard className="h-8 w-8 text-ui-text-secondary" />
                  <p>{t("previz.uploadHint")}</p>
                  <p className="text-xs text-ui-text-secondary">{t("previz.uploadHintDetail")}</p>
                  {recentScenes.length > 0 ? (
                    <div className="flex flex-wrap items-start justify-center gap-2">
                      {recentScenes.map((scene) => {
                        const cover = scene.thumb_url ?? scene.poster_url
                        return (
                          <button
                            key={scene.id}
                            type="button"
                            disabled={previzBusy}
                            title={scene.name}
                            className="flex w-16 flex-col items-center gap-1 disabled:opacity-50"
                            onClick={() => void openSceneForRecord(scene.id)}
                          >
                            <span className="aspect-video w-full overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken">
                              {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : null}
                            </span>
                            <span className="line-clamp-2 w-full text-[10px] leading-tight text-ui-text-secondary">
                              {scene.name}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="self-start"
                      disabled={previzBusy}
                      onClick={() => previzFileRef.current?.click()}
                    >
                      {previzBusy ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UploadCloud className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {t("previz.uploadButton")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="self-start"
                      disabled={previzBusy}
                      onClick={() => setFrameLibraryTarget("previz")}
                    >
                      <FolderOpen className="mr-1.5 h-3.5 w-3.5" />
                      {t("previz.pickFromLibrary")}
                    </Button>
                    <IconButtonGroup className="self-start">
                      <ActionHintTooltip label={t("previz.selectScene")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full gap-1.5 rounded-none border-0 px-2.5"
                          disabled={previzBusy}
                          onClick={() => setScenePickerOpen(true)}
                        >
                          <Box className="h-3.5 w-3.5" aria-hidden />
                          {t("previz.selectScene")}
                        </Button>
                      </ActionHintTooltip>
                      <ActionHintTooltip label={t("previz.createScene")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-full rounded-none border-0 px-2.5"
                          disabled={previzBusy}
                          aria-label={t("previz.createScene")}
                          onClick={() => void handleRecordPreviz()}
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </ActionHintTooltip>
                    </IconButtonGroup>
                  </div>
                  {showPrevizPrep ? (
                    <div
                      className="absolute inset-0 z-10"
                      onDragOver={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onDrop={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      <AssetPreviewBusyDim />
                      <AssetPreviewBusySpinner />
                    </div>
                  ) : null}
                </div>
              )}
              {showPrevizPrep && previzPrepStatus && !shot.previz ? (
                <ShotGenerationStatusRow status={previzPrepStatus} expandable={false} />
              ) : null}
              {previzError && <p className="text-center text-[11px] text-ui-status-failed">{previzError}</p>}
            </div>
          ) : null}
        </div>

        {/* ── References strip (characters, props, location, video frames) ── */}
        {(shotChars.length > 0 ||
          shot.reference_images.some((r) => r.type === "prop") ||
          shotLocation ||
          linkedScene ||
          hasVideo) && (
          <ShotReferencesPanel
            className="mt-3"
            projectId={projectId}
            onOpenCharacters={() => setLeftPanel("characters")}
            onOpenProps={() => setLeftPanel("props")}
            onOpenLocation={() => setLeftPanel("location")}
            scene3d={linkedScene}
            onOpenScene3d={() => void openLinkedScene()}
            characters={shotChars.map((ch) => shotRefCharacter(shot.reference_images, ch.name, ch.outfit_name))}
            props={shotRefProps(shot.reference_images)}
            location={shotRefLocation(shot.reference_images, shot.location_name, shot.location_variant_name)}
            videoFrames={[
              shot.video_first_frame_url ? { url: shot.video_first_frame_url, label: t("common.first") } : null,
              shot.video_last_frame_url ? { url: shot.video_last_frame_url, label: t("common.last") } : null,
            ].filter((frame): frame is { url: string; label: string } => frame != null)}
            onExtractFrames={
              hasVideo && !shot.video_first_frame_url && !shot.video_last_frame_url ? handleExtractFrames : undefined
            }
            extracting={busy === "extractFrames"}
            extractDisabled={!!busy}
          />
        )}

        {/* Полоса скриншотов видна и без видео: шот мог его лишиться, а кадры остались. */}
        {captures.length > 0 ? (
          <div className="mt-3">
            <ShotFrameCapturesStrip
              projectId={projectId}
              captures={captures}
              aspectClass={ff.aspect}
              canEdit={canEditContent}
              onDelete={(capture) => void handleDeleteCapture(capture)}
            />
          </div>
        ) : null}

        <div ref={extrasSlotRef} className="mt-3 empty:hidden" />
      </div>
      {storyboardGenerateHost && shot && !isTransition
        ? createPortal(storyboardGenerateNode, storyboardGenerateHost)
        : null}
      {videoGenerateHost && videoGenerateNode ? createPortal(videoGenerateNode, videoGenerateHost) : null}
      {shootingBoardGenerateHost ? createPortal(shootingBoardGenerateButton, shootingBoardGenerateHost) : null}

      {/* ── Zoom overlay ── */}
      {zoomedFrame && (
        <div
          className="fixed inset-0 z-[200] flex cursor-zoom-out items-center justify-center bg-[var(--ui-overlay-heavy)] backdrop-blur-sm"
          onClick={() => setZoomedFrame(null)}
        >
          <img src={zoomedFrame} alt="" className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl" />
        </div>
      )}

      {previewLightbox === "storyboard" && shot && storyboardSketchUrl ? (
        <SketchDialog
          open
          onOpenChange={(open) => {
            if (!open) setPreviewLightbox(null)
          }}
          projectId={projectId}
          projectFrameFormat={project.frame_format}
          sketchEdit={sketchEdit}
          sourceUrl={storyboardSketchUrl}
          initialFileName={`storyboard_shot${shot.number}`}
          headerTitle={t("tabs.storyboard")}
          presentation="view"
          persist="confirm"
          confirmAsSave
          nested
          onConfirm={handleConfirmStoryboardSketch}
          onCorrectionPendingChange={handleStoryboardSketchPending}
          replaceShotOnCorrect={{ shotId: shot.id, tab: "storyboard" }}
        />
      ) : null}

      {previewLightbox === "storyboard" && shot && !storyboardSketchUrl && shot.storyboard_preview_url ? (
        <ImageLightboxDialog
          open
          onOpenChange={(open) => {
            if (!open) setPreviewLightbox(null)
          }}
          url={shot.storyboard_preview_url}
          title={t("tabs.storyboard")}
          downloadUrl={shot.storyboard_preview_url}
          downloadFilename={`storyboard_shot${shot.number}.png`}
          copyable
          nested
        />
      ) : null}

      {previewLightbox === "shooting_board" && shot && shootingBoardSketchUrl ? (
        <SketchDialog
          open
          onOpenChange={(open) => {
            if (!open) setPreviewLightbox(null)
          }}
          projectId={projectId}
          projectFrameFormat={project.frame_format}
          sketchEdit={sketchEdit}
          sourceUrl={shootingBoardSketchUrl}
          initialFileName={`shooting_board_shot${shot.number}`}
          headerTitle={t("tabs.shootingBoard")}
          presentation="view"
          persist="confirm"
          confirmAsSave
          nested
          onConfirm={handleConfirmShootingBoardSketch}
          onCorrectionPendingChange={handleShootingBoardSketchPending}
          replaceShotOnCorrect={{ shotId: shot.id, tab: "shooting_board" }}
        />
      ) : null}

      {previewLightbox === "shooting_board" && shot && !shootingBoardSketchUrl && shootingBoardPreviewUrlEffective ? (
        <ImageLightboxDialog
          open
          onOpenChange={(open) => {
            if (!open) setPreviewLightbox(null)
          }}
          url={shootingBoardPreviewUrlEffective}
          title={t("tabs.shootingBoard")}
          downloadUrl={shootingBoardPreviewUrlEffective}
          downloadFilename={`shooting_board_shot${shot.number}.png`}
          copyable
          nested
        />
      ) : null}

      <PostProductionLaunchDialog
        open={postProductionOpen}
        onOpenChange={setPostProductionOpen}
        projectId={projectId}
        shotId={shotId}
        sourceVersionId={shot.post_production_source_version_id ?? shot.active_video_version_id}
        onSubmitted={handlePostProductionSubmitted}
      />

      <Scene3dPickerDialog
        open={scenePickerOpen}
        onOpenChange={setScenePickerOpen}
        projectId={projectId}
        shotLocationId={shot.location_id}
        nested
        onSelect={(scene) => void openSceneForRecord(scene.id)}
      />
      {/* Запись превиза: сначала настройки сцены, затем редактор. */}
      <LocationScene3dCreateDialog
        open={previzCreateOpen}
        onOpenChange={setPrevizCreateOpen}
        projectId={projectId}
        locationId={shot.location_id}
        locationName={shot.location_name ?? t("previz.defaultSceneName")}
        frameFormat={project.frame_format}
        nested
        onCreated={(scene) => {
          setPrevizRecordScene3dId(scene.id)
          setPrevizRecordSceneName(scene.name)
          setPrevizRecordPrevizId(null)
          setPrevizRecordInitialState(scene.scene_state ?? null)
          setPrevizCreateOpen(false)
          setCaptureHints(true)
          setPrevizRecordOpen(true)
        }}
      />
      {previzSceneData ? (
        <LocationScene3dDialog
          ref={scene3dDialogRef}
          open={previzRecordOpen}
          onOpenChange={(open) => {
            setPrevizRecordOpen(open)
            if (!open) {
              setPrevizRecordScene3dId(null)
              setPrevizRecordSceneName("")
              setPrevizRecordPrevizId(null)
              setPrevizRecordInitialState(null)
              setCaptureHints(false)
            }
          }}
          projectId={projectId}
          locationId={shot.location_id}
          locationName={shot.location_name ?? t("previz.defaultSceneName")}
          cast={previzSceneData.cast}
          props={previzSceneData.props}
          canEdit={canEditContent}
          frameFormat={project.frame_format}
          shotId={shot.id}
          presetCharacterIds={shot.characters.map((c) => c.id)}
          sceneMode={previzRecordScene3dId != null ? "editPreviz" : "newPreviz"}
          scene3dId={previzRecordScene3dId}
          previzId={previzRecordPrevizId}
          initialSceneState={previzRecordInitialState}
          initialHasVideo={Boolean(previzRecordPrevizId && shot.previz?.url)}
          initialHasPoster={Boolean(previzRecordPrevizId && shot.previz?.poster_url)}
          sceneName={previzRecordSceneName}
          onSceneNameSaved={setPrevizRecordSceneName}
          dismissAfterPrevizSave
          confirmUnsavedPreviz
          onPrevizCapture={() => {
            setPrevizPrep({
              startedAt: new Date().toISOString(),
              baselineId: shot.previz?.id ?? null,
            })
          }}
          onPrevizSaveFailed={() => {
            setPrevizPrep(null)
            fetchShot()
          }}
          nested
          resetPlacement="shell"
          captureHints={captureHints}
          onPrevizSaved={() => {
            setPrevizRecordOpen(false)
            setPrevizRecordScene3dId(null)
            setPrevizRecordSceneName("")
            setPrevizRecordPrevizId(null)
            setPrevizRecordInitialState(null)
            onDataChanged()
            fetchShot()
          }}
        />
      ) : null}

      {/* Один экземпляр пикера на оба назначения; какое из них главное — решает frameLibraryTarget. */}
      <LibraryPickerDialog
        open={frameLibraryTarget != null}
        onOpenChange={(open) => {
          if (!open) setFrameLibraryTarget(null)
        }}
        projectId={projectId}
        frameFormat={project.frame_format}
        frameRate={project.effective_video_output_fps}
        initialShotId={shotId}
        // Привязка сегмента: свободные сегменты лежат в узле «Аниматик» эпизода —
        // открываемся сразу там, а не в папке шота.
        initialAnimaticEpisodeId={frameLibraryTarget === "animatic" ? readEpisodeIdFromUrl(page.url) : null}
        // Привязка превиза: превизы лежат в собственной секции библиотеки.
        initialPrevizes={frameLibraryTarget === "previz"}
        subtitle={frameLibrarySubtitle}
        // У перехода свои назначения: опорник и раскадровку сервер ему не даёт,
        // поэтому предлагать их вместе с половинами бессмысленно. Вкладки
        // «Аниматик» и «Превиз» — отдельные режимы с единственным назначением.
        targets={
          frameLibraryTarget === "animatic"
            ? [{ id: "animatic", label: t("common.assignAnimaticSegment"), accepts: ANIMATIC_PICK_ACCEPTS }]
            : frameLibraryTarget === "previz"
              ? [{ id: "previz", label: t("previz.assign"), accepts: PREVIZ_PICK_ACCEPTS }]
              : frameLibraryTarget === "video"
                ? [{ id: "video", label: t("frameLibrary.assignVideo"), accepts: ["video"] }]
                : isTransition
                  ? [
                      { id: "transition_left", label: t("transitionFrames.assignLeft"), accepts: ["image"] },
                      { id: "transition_right", label: t("transitionFrames.assignRight"), accepts: ["image"] },
                    ]
                  : [
                      { id: "storyboard", label: t("frameLibrary.assignStoryboard"), accepts: ["image"] },
                      { id: "shooting_board", label: t("frameLibrary.assignShootingBoard"), accepts: ["image"] },
                    ]
        }
        // Уже привязанный сегмент гасим: повторная привязка — no-op, а плитка без
        // объяснения выглядела бы кликабельной.
        pickedKeys={
          frameLibraryTarget === "animatic" && shot.animatic_segment
            ? animaticPickedKeys
            : frameLibraryTarget === "previz" && shot.previz
              ? previzPickedKeys
              : undefined
        }
        defaultTargetId={frameLibraryTarget ?? (isTransition ? "transition_left" : "storyboard")}
        busyTargetId={
          transitionBusySide === "left"
            ? "transition_left"
            : transitionBusySide === "right"
              ? "transition_right"
              : busy === "storyboardFromSource"
                ? "storyboard"
                : busy === "shootingBoardFromSource"
                  ? "shooting_board"
                  : busy === "videoFromSource"
                    ? "video"
                    : animaticLinking
                      ? "animatic"
                      : previzBusy
                        ? "previz"
                        : null
        }
        onPick={(targetId, source) => {
          if (targetId === "animatic") void handleAnimaticFromLibrary(source)
          else if (targetId === "previz") void handlePrevizFromLibrary(source)
          else if (targetId === "transition_left") void handleTransitionFrameFromSource("left", source)
          else if (targetId === "transition_right") void handleTransitionFrameFromSource("right", source)
          else if (targetId === "shooting_board") void handleShootingBoardFromSource(source)
          else if (targetId === "video") void handleVideoFromSource(source)
          else void handleStoryboardFromSource(source)
        }}
      />

      {seedanceVideoConsentDialog}

      <FirstGenResolutionDialog
        open={firstGenResolutionOpen}
        onOpenChange={setFirstGenResolutionOpen}
        effectiveResolution={effectiveResolution}
        minOption={firstGenMinOption}
        busy={!!busy}
        onGenerate={(videoResolutionOverride) => void runGenerateVideoRequest(videoResolutionOverride)}
      />
    </>
  )
}
